/**
 * Frontend Admin API Service - Matching Legacy Database Schema
 * =============================================================================
 */

import axios from 'axios';
import type { ProductFileMeta } from '@happy3d/materials/productMaterials';
import type {
  User, Product, Order, OrderDetail, 
  DashboardStats, PaginatedResponse, OrderFilters, 
  UserFilters, Language, Currency, LoginResponse
} from '../types';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  withCredentials: true,
});

// Request interceptor for auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// =============================================================================
// AUTH ENDPOINTS
// =============================================================================

export const authApi = {
  login: (email: string, password: string) =>
    api.post<LoginResponse>('/auth/login', { email, password }),
  
  logout: () => api.post('/auth/logout'),
  
  me: () => api.get<LoginResponse['user']>('/auth/me'),
  
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
  
  forgotPassword: (email: string) =>
    api.post<{ message: string }>('/auth/forgot-password', { email }),
  
  resetPassword: (email: string, code: string, newPassword: string) =>
    api.post<{ message: string }>('/auth/reset-password', { email, code, newPassword }),
  
  updateLanguage: (language: string) =>
    api.patch<{ message: string; language: string }>('/auth/language', { language }),
};

// =============================================================================
// DASHBOARD ENDPOINTS
// =============================================================================

export const dashboardApi = {
  getStats: () => api.get<DashboardStats>('/admin/dashboard'),
};

// =============================================================================
// ORDERS ENDPOINTS
// Uses orderId (integer) as identifier
// =============================================================================

export const ordersApi = {
  list: (params?: OrderFilters & { page?: number; limit?: number }) =>
    api.get<PaginatedResponse<Order>>('/admin/orders', { params }),
  
  get: (orderId: number) => 
    api.get<OrderDetail>(`/admin/orders/${orderId}`),
  
  updateStatus: (orderId: number, status: string) =>
    api.patch<{ orderId: number; status: string }>(`/admin/orders/${orderId}/status`, { status }),
  
  delete: (orderId: number) => 
    api.delete(`/admin/orders/${orderId}`),
};

// =============================================================================
// PRODUCTS ENDPOINTS
// Uses productId (integer) as identifier
// =============================================================================

// =============================================================================
// PRODUCTS ENDPOINTS - Full Product Management
// =============================================================================

export interface ProductListItem {
  id: number;
  code: string | null;
  name: string;
  description: string;
  productType: string | null;
  group: { id: number; name: string } | null;
  unit: string;
  sortOrder: number;
  is3D: boolean;
  isNew: boolean;
  isInSale: boolean;
  isFrontPage: boolean;
  isVisible: boolean;
  subProductCount: number;
  relatedProductCount: number;
  discountPercent: number | null;
  created: string | null;
}

export interface ProductTexts {
  [languageId: string]: {
    name: string;
    description: string;
  };
}

export interface ProductPropertyValue {
  propertyId: number;
  propertyName: string;
  value: string | null;
}

export interface ProductDetail {
  id: number;
  code: string | null;
  productType: string | null;
  groupId: number | null;
  group: { id: number; name: string } | null;
  unit: string;
  is3D: boolean;
  isNew: boolean;
  isInSale: boolean;
  isFrontPage: boolean;
  isVisible: boolean;
  discountPercent: number | null;
  texts: ProductTexts;
  properties: ProductPropertyValue[];
  subProductCount: number;
  relatedProductCount: number;
  created: string | null;
  // Perustiedot fields (legacy parity)
  designProtection?: string | null;
  sellingUnitId?: string | null;
  productOrder?: number | null;
  videoUrl?: string | null;
  isVisibleForAccount?: boolean;
  showUploadButton?: boolean;
  hasDesignFiles?: boolean;
  createSticker?: boolean;
  edgeColor?: string | null;
  // 3D control data (legacy "3D-ohjaustiedot"); position is the legacy
  // "x,y,z,ry" string, not the Product3DModel vector.
  position?: string | null;
  pdfWidth?: number;
  pdfHeight?: number;
  pdfValue?: string | null;
  pdfMapping?: string | null;
  moveXup?: number;
  moveXdown?: number;
  moveZup?: number;
  moveZdown?: number;
  rotationYup?: number;
  rotationYdown?: number;
  // Base pricing fields (legacy Hinnat tab header)
  costPrice?: number | null;
  clientPrice?: number | null;
  startCost?: number | null;
  sellPriceTarget?: number | null;
  pricingMethod?: string | null;
  showPrice?: boolean;
  useLotPricing?: boolean;
}

export interface ProductUnit {
  id: string;
  name: string;
  code: string;
  show: boolean;
}

// --- Product info parity types (tabs 3-11) ---------------------------------

export interface ProductAttributeRow {
  propertyId: number;
  label: string;
  isLink: boolean;
  value: string | null;
  sortOrder: number;
}

export interface PropertyGroupOption {
  id: string;
  label: string;
  propertyIds: number[];
}

export interface ProductPickItem {
  id: number;
  name: string;
}

export interface RelatedProductRow {
  id: string;
  relatedProductId: number;
  name: string;
  sortOrder: number;
}

export interface SubProductRow {
  id: string;
  subProductId: number;
  name: string;
  quantity: number;
  price: number | null;
  isMain: boolean;
  replaceable: boolean;
  sortOrder: number;
}

export interface ProductMaterialRow {
  id: string;
  materialId: number;
  name: string | null;
  code: string | null;
  width: number | null;
  length: number | null;
  amount: number | null;
  quantity: number | null;
  divider: number | null;
  sortOrder: number;
}

export interface OperationResourceRow {
  id: string;
  resourceId: number;
  resourceName: string;
  resourceUnit?: string | null;
  plannedTime: number;
}

export interface ProductOperationRow {
  id: string;
  rowNumber: number;
  plannedTime: number;
  name: string;
  nameForLanguage: string;
  resources: OperationResourceRow[];
}

export interface ProductPriceTier {
  id: string;
  currencyId: string;
  minimumAmount: number;
  sellPrice: number;
  listPrice: number | null;
  startCost: number;
  unitCostPrice: number | null;
  unitPrice: number | null;
  unitPriceDiscountPercent: number | null;
}

export interface CurrencyInfo {
  id: string;
  name: string;
  rateEuro: number;
  symbol: string;
  isDefault: boolean;
  decimalDigits: number;
}

export interface ProductImageRow {
  id: string;
  fileName: string;
  originalFileName: string | null;
  mimeType: string | null;
  fileBytes: number | null;
  sortOrder: number;
  path: string;
}

// Hinnoittele cost panel row (ProductCostItems RESOURCE / MATERIAL / OTHER).
// Row total = amount * multiplier * price / divider (legacy formula).
export interface PricingCostRow {
  id: string;
  itemType: 'RESOURCE' | 'MATERIAL' | 'OTHER';
  sortOrder: number;
  name: string | null;
  width: number | null;
  length: number | null;
  amount: number;
  unitId: string | null;
  startCost: number | null;
  costPrice: number;
  salesMarginPercent: number;
  sellPrice: number;
  multiplier: number;
  divider: number;
  unitsPerHour: number | null;
  costPerHour: number | null;
  resourceId: number | null;
  materialId: number | null;
}

export interface PricingCosts {
  resources: PricingCostRow[];
  materials: PricingCostRow[];
  others: PricingCostRow[];
}

export interface ProductAccountAssignment {
  accountId: number;
  name: string | null;
  code: string | null;
}

// Fields shared by product create/update calls (legacy Perustiedot parity).
export interface ProductBasicsPayload {
  code?: string;
  productType?: string;
  groupId?: number;
  is3D?: boolean;
  isNew?: boolean;
  isInSale?: boolean;
  isFrontPage?: boolean;
  isVisible?: boolean;
  discountPercent?: number;
  texts?: ProductTexts;
  properties?: { propertyId: number; value: string }[];
  designProtection?: string;
  sellingUnitId?: string;
  productOrder?: number | null;
  videoUrl?: string;
  isVisibleForAccount?: boolean;
  showUploadButton?: boolean;
  hasDesignFiles?: boolean;
  createSticker?: boolean;
  edgeColor?: string | null;
  position?: string;
  pdfWidth?: number;
  pdfHeight?: number;
  pdfValue?: string;
  pdfMapping?: string;
  moveXup?: number;
  moveXdown?: number;
  moveZup?: number;
  moveZdown?: number;
  rotationYup?: number;
  rotationYdown?: number;
  // Base pricing fields (Hinnat tab header)
  costPrice?: number | null;
  clientPrice?: number | null;
  startCost?: number | null;
  sellPriceTarget?: number | null;
  pricingMethod?: string;
  showPrice?: boolean;
  useLotPricing?: boolean;
}

export interface Product3DModel {
  id: number;
  is3D: boolean;
  model3DPath: string | null;
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number };
  scale: number;
  babylonData: string | null;
  /** Product can be snapped to other wall segments in the 3D Designer. */
  isWallProduct: boolean;
}

export interface ProductFile {
  id: string;
  name: string;
  originalFileName: string | null;
  fileName: string | null;
  mimeType: string | null;
  fileBytes: number | null;
  location: string | null;
  sortOrder: number;
  createdAt: string;
  // Live missing-on-disk check (legacy IsMissing): null when the file's group
  // directory could not be read, so the UI only marks files red on a real miss.
  isMissing?: boolean | null;
  // Path relative to the NAS base, e.g. "assets/docs/esitetasku2_1.pdf".
  // Built from the file's group directory - never assume a flat assets/ dir.
  relativePath?: string;
  groupId: string | null;
  groupCode: string | null;
  groupName: string | null;
  // Texture / design metadata (legacy 3D-tiedostot columns)
  meshWidth?: number;
  meshHeight?: number;
  meshCount?: number;
  topBleed?: number;
  rightBleed?: number;
  bottomBleed?: number;
  leftBleed?: number;
  horizontalOffset?: number;
  verticalOffset?: number;
  isBraid?: boolean;
  replacementProductFileId?: string | null;
}

// Build an authenticated URL for serving a NAS file (img/src, 3D loaders).
// The upload route accepts the JWT as a ?token= query param because <img>
// tags cannot send an Authorization header.
export const nasFileUrl = (path: string): string => {
  const base = import.meta.env.VITE_API_URL || '/api';
  const token = localStorage.getItem('adminToken') || '';
  return `${base}/upload/file?path=${encodeURIComponent(path)}&token=${encodeURIComponent(token)}`;
};

/**
 * Where a 3D loader or a texture fetches a stored file (Update_181). Neither can
 * send the Authorization header, so a file straight in the NAS assets folder goes
 * through the public scene-asset route, as the product models do, and anything
 * else through /upload/file with the login (nasFileUrl).
 */
export const sceneFileUrl = (stored: string): string => {
  const rel = stored.replace(/\\/g, '/').replace(/^\/+/, '');
  const shipped = /^assets\/([^/]+)$/.exec(rel);
  const base = import.meta.env.VITE_API_URL || '/api/v1';
  return shipped ? `${base}/upload/scene-asset/${encodeURIComponent(shipped[1])}` : nasFileUrl(rel);
};

/**
 * How a 3D loader fetches a stored model. Straight in the NAS assets folder:
 * through scene-asset as a folder, so the textures and .bin files a model names
 * load from next to it, as the product models do. Anywhere else: through
 * /upload/file with the login, which suits a self-contained .glb or .stl; the
 * loader is chosen by the file's extension, as the address ends in a query.
 */
export const sceneModelSource = (stored: string): { rootUrl: string; file: string; extension?: string } => {
  const rel = stored.replace(/\\/g, '/').replace(/^\/+/, '');
  const shipped = /^assets\/([^/]+)$/.exec(rel);
  if (shipped) return { rootUrl: `${import.meta.env.VITE_API_URL || '/api/v1'}/upload/scene-asset/`, file: shipped[1] };
  return { rootUrl: '', file: nasFileUrl(rel), extension: /\.[a-z0-9]+$/i.exec(rel)?.[0].toLowerCase() };
};

/**
 * A floor or wall texture's image: a texture shipped in assets/ through
 * scene-asset, an uploaded one through its own route, which reads it from where
 * it was written (before Update_181 uploaded textures never showed).
 */
export const textureFileUrl = (texture: { id?: string; filePath?: string } | null | undefined, type: 'system' | 'user' | 'custom' | null | undefined): string | null => {
  const filePath = texture?.filePath?.replace(/\\/g, '/').replace(/^\/+/, '') ?? '';
  if (/^assets\/[^/]+$/.test(filePath)) return sceneFileUrl(filePath);
  if ((type === 'system' || type === 'user') && texture?.id) {
    const base = import.meta.env.VITE_API_URL || '/api/v1';
    const token = localStorage.getItem('adminToken') || '';
    return `${base}/upload/texture/${type}/${encodeURIComponent(texture.id)}?token=${encodeURIComponent(token)}`;
  }
  return filePath ? nasFileUrl(filePath) : null;
};

export interface ProductFileGroup {
  id: string;
  code: string;
  path: string | null;
  name: string;
}

// Legacy 3D-tiedostot section: a BaseProductFiles group with its files
// (FileName order), rendered as a numbered section in the Files tab.
export interface ProductFileGroupWithFiles extends ProductFileGroup {
  files: ProductFile[];
  // False when the group's directory on the NAS could not be read (not
  // mounted, or the folder does not exist yet).
  directoryReadable?: boolean;
}

export interface SceneCatalogProduct {
  id: number;
  name: string;
  productType: string | null;
  code: string | null;
  /** Bare filename, served publicly via /upload/scene-asset/ (no token needed). */
  modelFile: string;
  /** NAS-relative path for nasFileUrl(), or null when the product has no render. */
  image: string | null;
}

export interface SceneCatalogGroup {
  id: number;
  name: string;
  products: SceneCatalogProduct[];
}

export interface ProductFilters {
  search?: string;
  groupId?: number;
  is3D?: boolean | string;
  isNew?: boolean | string;
  isInSale?: boolean | string;
  isFrontPage?: boolean | string;
  language?: string;
}

// Product Costing Types
export interface CostItemEmployee {
  id: number;
  name: string;
  hourlySalary: number;
}

export interface CostItemResource {
  id: number;
  name: string;
  costPerHour: number;
  costPerUnit: number;
  capacityUnit: string | null;
}

export interface CostItemMaterial {
  id: number;
  name: string;
  code: string;
  purchasePrice: number;
  salesPrice: number;
  purchaseUnit: string | null;
}

export interface ProductCostItem {
  id: string;
  itemType: 'EMPLOYEE' | 'RESOURCE' | 'MATERIAL';
  description: string | null;
  quantity: number;
  unitCost: number | null;
  unitPrice: number | null;
  totalCost: number;
  totalPrice: number;
  sortOrder: number;
  employee: CostItemEmployee | null;
  resource: CostItemResource | null;
  material: CostItemMaterial | null;
}

export interface ProductCostingSummary {
  employeeCost: number;
  resourceCost: number;
  materialCost: number;
  materialSalesValue: number;
  totalProductionCost: number;
  markupPercent: number;
  calculatedPrice: number;
  currentCostPrice: number | null;
  currentClientPrice: number | null;
  useManualPrice: boolean;
}

export interface ProductCostingData {
  productId: number;
  costItems: ProductCostItem[];
  summary: ProductCostingSummary;
}

export interface CostingAvailableItems {
  employees: CostItemEmployee[];
  resources: CostItemResource[];
  materials: CostItemMaterial[];
}

export const productsApi = {
  // The 3D Designer's own catalogue: every 3D product that actually has a loadable
  // model, grouped by category. Deliberately separate from getGroupsForScene, which
  // is the CUSTOMER-facing curation (Group.Menu = 3) that the live legacy site reads.
  getSceneCatalog: (language?: string) =>
    api.get<{ groups: SceneCatalogGroup[]; totalProducts: number }>(
      '/admin/scene-catalog', { params: language ? { language } : undefined },
    ),

  list: (params?: ProductFilters & { page?: number; limit?: number }) =>
    api.get<PaginatedResponse<ProductListItem>>('/admin/products', { params }),
  
  get: (productId: number) => 
    api.get<ProductDetail>(`/admin/products/${productId}`),
  
  create: (data: ProductBasicsPayload) =>
    api.post<{ message: string; product: { id: number; code: string } }>('/admin/products', data),

  update: (productId: number, data: ProductBasicsPayload) =>
    api.put<{ message: string }>(`/admin/products/${productId}`, data),

  getUnits: () =>
    api.get<{ data: ProductUnit[] }>('/admin/products/units/list'),

  // Customer accounts panel ("Tuotteen asiakastilit")
  getAccounts: (productId: number) =>
    api.get<{ data: ProductAccountAssignment[] }>(`/admin/products/${productId}/accounts`),

  addAccount: (productId: number, accountId: number) =>
    api.post<{ message: string }>(`/admin/products/${productId}/accounts/${accountId}`),

  removeAccount: (productId: number, accountId: number) =>
    api.delete<{ message: string }>(`/admin/products/${productId}/accounts/${accountId}`),

  // --- Attributes on product (Tuoteominaisuudet tab) ---
  getProductAttributes: (productId: number, language?: string) =>
    api.get<{ data: ProductAttributeRow[] }>(`/admin/products/${productId}/properties`, { params: { language } }),

  getPropertyGroups: (language?: string) =>
    api.get<{ data: PropertyGroupOption[] }>('/admin/products/property-groups/list', { params: { language } }),

  addProductAttribute: (productId: number, propertyId: number) =>
    api.post<{ message: string }>(`/admin/products/${productId}/properties/${propertyId}`),

  addProductAttributeGroup: (productId: number, groupId: string) =>
    api.post<{ message: string; added: number }>(`/admin/products/${productId}/properties/group/${groupId}`),

  updateProductAttribute: (productId: number, propertyId: number, data: { value?: string; sortOrder?: number }) =>
    api.put<{ message: string }>(`/admin/products/${productId}/properties/${propertyId}`, data),

  deleteProductAttribute: (productId: number, propertyId: number) =>
    api.delete<{ message: string }>(`/admin/products/${productId}/properties/${propertyId}`),

  // --- Product picklist (dropdowns) ---
  getPicklist: (language?: string) =>
    api.get<{ data: ProductPickItem[] }>('/admin/products/picklist', { params: { language } }),

  // --- Related products (Liittyvät tuotteet tab) ---
  getRelated: (productId: number, language?: string) =>
    api.get<{ data: RelatedProductRow[] }>(`/admin/products/${productId}/related`, { params: { language } }),

  addRelated: (productId: number, relatedId: number) =>
    api.post<{ message: string }>(`/admin/products/${productId}/related/${relatedId}`),

  updateRelated: (productId: number, rowId: string, data: { sortOrder?: number }) =>
    api.put<{ message: string }>(`/admin/products/${productId}/related/${rowId}`, data),

  deleteRelated: (productId: number, rowId: string) =>
    api.delete<{ message: string }>(`/admin/products/${productId}/related/${rowId}`),

  // --- Sub-products (Alatuotteet tab) ---
  getSubProducts: (productId: number, language?: string) =>
    api.get<{ data: SubProductRow[] }>(`/admin/products/${productId}/subproducts`, { params: { language } }),

  addSubProduct: (productId: number, subId: number) =>
    api.post<{ message: string }>(`/admin/products/${productId}/subproducts/${subId}`),

  updateSubProduct: (productId: number, rowId: string, data: { quantity?: number; isMain?: boolean; replaceable?: boolean; sortOrder?: number }) =>
    api.put<{ message: string }>(`/admin/products/${productId}/subproducts/${rowId}`, data),

  deleteSubProduct: (productId: number, rowId: string) =>
    api.delete<{ message: string }>(`/admin/products/${productId}/subproducts/${rowId}`),

  // --- Structure / BOM (Rakenne tab) ---
  getMaterials: (productId: number) =>
    api.get<{ data: ProductMaterialRow[] }>(`/admin/products/${productId}/materials`),

  addMaterial: (productId: number, materialId: number) =>
    api.post<{ message: string; id: string }>(`/admin/products/${productId}/materials/${materialId}`),

  updateMaterial: (productId: number, rowId: string, data: { width?: number | string | null; length?: number | string | null; amount?: number | string | null; quantity?: number | string | null; divider?: number | string | null; sortOrder?: number }) =>
    api.put<{ message: string }>(`/admin/products/${productId}/materials/${rowId}`, data),

  deleteMaterial: (productId: number, rowId: string) =>
    api.delete<{ message: string }>(`/admin/products/${productId}/materials/${rowId}`),

  copyMaterialsFrom: (productId: number, sourceId: number) =>
    api.post<{ message: string; copied: number }>(`/admin/products/${productId}/materials/copy-from/${sourceId}`),

  // --- Work phases (Työvaiheet tab) ---
  getOperations: (productId: number, language?: string, accountId?: number | null) =>
    api.get<{ data: ProductOperationRow[] }>(`/admin/products/${productId}/operations`, {
      params: { language, accountId: accountId ?? undefined },
    }),

  addOperation: (productId: number, data: { rowNumber?: number | string; plannedTime?: number | string; name?: string; languageId?: string }) =>
    api.post<{ message: string; id: string }>(`/admin/products/${productId}/operations`, data),

  updateOperation: (productId: number, opId: string, data: { rowNumber?: number | string; plannedTime?: number | string; name?: string; languageId?: string }) =>
    api.put<{ message: string }>(`/admin/products/${productId}/operations/${opId}`, data),

  deleteOperation: (productId: number, opId: string) =>
    api.delete<{ message: string }>(`/admin/products/${productId}/operations/${opId}`),

  copyOperationsFrom: (productId: number, sourceId: number) =>
    api.post<{ message: string; copied: number }>(`/admin/products/${productId}/operations/copy-from/${sourceId}`),

  addOperationResource: (productId: number, opId: string, data: { resourceId: number; plannedTime?: number | string; accountId?: number | null }) =>
    api.post<{ message: string; id: string }>(`/admin/products/${productId}/operations/${opId}/resources`, data),

  updateOperationResource: (productId: number, opId: string, rowId: string, data: { plannedTime?: number | string }) =>
    api.put<{ message: string }>(`/admin/products/${productId}/operations/${opId}/resources/${rowId}`, data),

  deleteOperationResource: (productId: number, opId: string, rowId: string) =>
    api.delete<{ message: string }>(`/admin/products/${productId}/operations/${opId}/resources/${rowId}`),

  // --- Price tiers + currencies (Hinnat tab) ---
  getCurrencies: () =>
    api.get<{ data: CurrencyInfo[] }>('/admin/products/currencies/list'),

  getPrices: (productId: number) =>
    api.get<{ data: ProductPriceTier[] }>(`/admin/products/${productId}/prices`),

  addPriceTier: (productId: number, minimumAmount: number) =>
    api.post<{ message: string; id: string }>(`/admin/products/${productId}/prices`, { minimumAmount }),

  updatePriceTier: (productId: number, priceId: string, data: Partial<Omit<ProductPriceTier, 'id' | 'currencyId'>>) =>
    api.put<{ message: string }>(`/admin/products/${productId}/prices/${priceId}`, data),

  deletePriceTier: (productId: number, minimumAmount: number) =>
    api.delete<{ message: string; deleted: number }>(`/admin/products/${productId}/prices/amount/${minimumAmount}`),

  calculateCurrencyPrices: (productId: number, rounding: number) =>
    api.post<{ message: string; updated: number }>(`/admin/products/${productId}/prices/calculate`, { rounding }),

  // --- Hinnoittele cost panel (Resurssitarpeet / Materiaalit / Muut) ---
  getPricingCosts: (productId: number) =>
    api.get<PricingCosts>(`/admin/products/${productId}/pricing-costs`),

  addPricingResource: (productId: number, resourceId: number) =>
    api.post<PricingCosts>(`/admin/products/${productId}/pricing-costs/resource/${resourceId}`),

  addPricingMaterial: (productId: number, materialId: number) =>
    api.post<PricingCosts>(`/admin/products/${productId}/pricing-costs/material/${materialId}`),

  addPricingOther: (productId: number, name: string) =>
    api.post<PricingCosts>(`/admin/products/${productId}/pricing-costs/other`, { name }),

  updatePricingCost: (productId: number, itemId: string, data: Partial<{
    sortOrder: number | string; name: string;
    width: number | string; length: number | string; amount: number | string;
    unitId: string | null; startCost: number | string;
    costPrice: number | string; salesMarginPercent: number | string; sellPrice: number | string;
    multiplier: number | string; divider: number | string;
    unitsPerHour: number | string; costPerHour: number | string;
  }>) => api.put<PricingCosts>(`/admin/products/${productId}/pricing-costs/${itemId}`, data),

  deletePricingCost: (productId: number, itemId: string) =>
    api.delete<PricingCosts>(`/admin/products/${productId}/pricing-costs/${itemId}`),

  importPricingResources: (productId: number) =>
    api.post<PricingCosts>(`/admin/products/${productId}/pricing-costs/import-resources`),

  importPricingMaterials: (productId: number) =>
    api.post<PricingCosts>(`/admin/products/${productId}/pricing-costs/import-materials`),

  saveOfferPrice: (productId: number, data: { quantity: number; unitCost: number; unitSale: number; baseUnitSale: number; sellPriceTarget: number }) =>
    api.post<{ message: string }>(`/admin/products/${productId}/prices/offer`, data),

  updateSellPriceTarget: (productId: number, value: number, updateListPrices: boolean) =>
    api.put<{ message: string }>(`/admin/products/${productId}/sell-price-target`, { value, updateListPrices }),

  // --- Product images (Kuvat tab) ---
  getImages: (productId: number) =>
    api.get<{ data: ProductImageRow[] }>(`/admin/products/${productId}/images`),

  uploadImages: (productId: number, files: File[], onProgress?: (percent: number) => void) => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    return api.post<{ message: string; images: { id: string; fileName: string; sortOrder: number }[] }>(
      `/admin/products/${productId}/images`,
      formData,
      {
        onUploadProgress: e => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        },
      },
    );
  },

  updateImageOrder: (productId: number, images: { id: string; sortOrder: number }[]) =>
    api.put<{ message: string }>(`/admin/products/${productId}/images/order`, { images }),

  deleteImage: (productId: number, imageId: string) =>
    api.delete<{ message: string }>(`/admin/products/${productId}/images/${imageId}`),

  // --- Product file upload + metadata (3D-tiedostot tab) ---
  uploadProductFiles: (productId: number, groupId: string, files: File[], onProgress?: (percent: number) => void) => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    return api.post<{ message: string; files: { id: string; fileName: string }[] }>(
      `/admin/products/${productId}/files/upload/${groupId}`,
      formData,
      {
        onUploadProgress: e => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        },
      },
    );
  },

  updateFileMetadata: (productId: number, fileId: string, data: {
    name?: string; fileName?: string; sortOrder?: number;
    meshWidth?: number | string; meshHeight?: number | string; meshCount?: number | string;
    topBleed?: number | string; rightBleed?: number | string; bottomBleed?: number | string; leftBleed?: number | string;
    horizontalOffset?: number | string; verticalOffset?: number | string;
    isBraid?: boolean; replacementProductFileId?: string | null;
  }) => api.put<{ message: string }>(`/admin/products/${productId}/files/${fileId}/metadata`, data),

  // --- 3D screenshots (3D-malli tab) ---
  saveScreenshot: (productId: number, suffix: string, imageBase64: string) =>
    api.post<{ message: string; fileId: string; fileName: string; path: string }>(
      `/admin/products/${productId}/files/screenshot`,
      { suffix, imageBase64 },
    ),
  
  delete: (productId: number) => 
    api.delete<{ message: string }>(`/admin/products/${productId}`),
  
  toggleFlag: (productId: number, flag: '3d' | 'new' | 'sale' | 'frontpage' | 'visible') =>
    api.post<{ id: number; field: string; value: boolean }>(`/admin/products/${productId}/toggle/${flag}`),
  
  translate: (text: string, sourceLanguage?: string, targetLanguages?: string[]) =>
    api.post<{ source: string; sourceText: string; translations: Record<string, string>; warnings?: string[] }>('/admin/products/translate', {
      text,
      sourceLanguage,
      targetLanguages,
    }),
  
  getTranslateStatus: () =>
    api.get<{ available: boolean; url?: string; error?: string; supportedLanguages?: string[] }>('/admin/products/translate/status'),
  
  getLanguages: () =>
    api.get<{ data: { id: string; name: string }[] }>('/admin/products/languages'),
  
  uploadImage: (productId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/admin/products/${productId}/image`, formData);
  },

  // 3D Model endpoints
  get3DModel: (productId: number) =>
    api.get<Product3DModel>(`/admin/products/${productId}/3d-model`),
  
  update3DModel: (productId: number, data: {
    model3DPath?: string;
    defaultPositionX?: number;
    defaultPositionY?: number;
    defaultPositionZ?: number;
    defaultRotationX?: number;
    defaultRotationY?: number;
    defaultRotationZ?: number;
    defaultScale?: number;
    is3D?: boolean;
    isWallProduct?: boolean;
  }) => api.patch<{ message: string }>(`/admin/products/${productId}/3d-model`, data),

  // Files endpoints
  getFiles: (productId: number, language?: string) =>
    api.get<{
      groups: ProductFileGroupWithFiles[];
      files: Record<string, ProductFile[]>;
      fileGroups: ProductFileGroup[];
    }>(`/admin/products/${productId}/files`, { params: language ? { language } : undefined }),

  // Auto-discover files on the NAS that match this product's identifiers and
  // import them into the ProductFiles table. Used when migration didn't carry
  // over the legacy file metadata but the assets do exist on disk.
  scanFiles: (productId: number) =>
    api.post<{
      scanned: number;
      matched: number;
      imported: number;
      skipped: number;
      modelPath?: string | null;
      identifiersUsed?: string[];
      warning?: string;
    }>(`/admin/products/${productId}/files/scan`),

  addFile: (productId: number, data: {
    name?: string;
    fileName: string;
    originalFileName?: string;
    mimeType?: string;
    fileBytes?: number;
    location?: string;
    groupId?: string;
  }) => api.post<{ message: string; file: { id: string; name: string; fileName: string } }>(`/admin/products/${productId}/files`, data),
  
  deleteFile: (productId: number, fileId: string) =>
    api.delete<{ message: string }>(`/admin/products/${productId}/files/${fileId}`),
  
  // Costing endpoints
  getCosting: (productId: number) =>
    api.get<ProductCostingData>(`/admin/products/${productId}/costing`),
  
  addCostItem: (productId: number, data: {
    itemType: 'EMPLOYEE' | 'RESOURCE' | 'MATERIAL';
    employeeId?: number;
    resourceId?: number;
    materialId?: number;
    description?: string;
    quantity: number;
  }) => api.post<{ message: string; costItem: { id: string; itemType: string; quantity: number; totalCost: number } }>(`/admin/products/${productId}/costing`, data),
  
  updateCostItem: (productId: number, itemId: string, data: {
    quantity?: number;
    description?: string;
    sortOrder?: number;
  }) => api.patch<{ message: string }>(`/admin/products/${productId}/costing/${itemId}`, data),
  
  deleteCostItem: (productId: number, itemId: string) =>
    api.delete<{ message: string }>(`/admin/products/${productId}/costing/${itemId}`),
  
  updatePricing: (productId: number, data: {
    costPrice?: number;
    clientPrice?: number;
    costMarkupPercent?: number;
    useManualPrice?: boolean;
  }) => api.patch<{ message: string }>(`/admin/products/${productId}/pricing`, data),
  
  getCostingAvailable: () =>
    api.get<CostingAvailableItems>('/admin/products/costing/available'),
};

// =============================================================================
// ACCOUNTS (admin list - used by the product customer-accounts panel)
// =============================================================================

export interface AdminAccountListItem {
  id: number;
  name: string | null;
  code: string | null;
  isReseller: boolean;
}

export const adminAccountsApi = {
  list: (params?: { search?: string; limit?: number; page?: number }) =>
    api.get<{ data: AdminAccountListItem[]; total: number }>('/admin/accounts', {
      params: { limit: 500, ...params },
    }),
};

// =============================================================================
// STORAGE SETTINGS ENDPOINTS
// =============================================================================

export interface StorageSettings {
  nasBasePath: string;
  nasAssetsPath: string;
  nas3DModelsPath: string;
  nasImagesPath: string;
  nasDesignFilesPath: string;
  nasProductionFilesPath: string;
}

// Values actually in use after fallbacks - what the server resolved the
// (possibly empty) stored settings to.
export interface EffectiveStoragePaths {
  basePath: string;
  assets: string;
  models: string;
  images: string;
  design: string;
  production: string;
}

// One probe result per directory. Keys are 'base' plus each product file
// group Code (DESIGN / PRODUCTION / PRODUCT3D / PICTURES / ...).
export interface StorageDirStatus {
  accessible: boolean;
  path: string;
  files?: number;
  error?: string;
}

export interface StorageBrowseItem {
  name: string;
  isDirectory: boolean;
  path: string;
  extension: string | null;
}

export interface StorageBrowseResult {
  basePath: string;
  currentPath: string;
  parentPath: string | null;
  items: StorageBrowseItem[];
}

export const storageApi = {
  getSettings: () =>
    api.get<StorageSettings & { effective: EffectiveStoragePaths }>('/admin/storage-settings'),

  updateSettings: (data: Partial<StorageSettings>) =>
    api.patch<{ message: string; effective: EffectiveStoragePaths }>('/admin/storage-settings', data),

  browse: (path?: string, type?: '3d' | 'images' | 'design' | 'production' | 'all') =>
    api.get<StorageBrowseResult>('/admin/storage-browse', { params: { path, type } }),

  getStatus: () =>
    api.get<{ status: Record<string, StorageDirStatus>; basePath: string }>('/admin/storage-status'),
};

// =============================================================================
// UPLOAD ENDPOINTS (with progress support via axios)
// =============================================================================

export interface UploadResponse {
  success: boolean;
  fileId?: string;
  fileName: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
}

export const uploadApi = {
  // Upload 3D model file
  upload3DModel: (
    file: File, 
    options?: { productId?: number; propModelId?: string; subfolder?: string },
    onProgress?: (percent: number, loaded: number, total: number) => void
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.productId) formData.append('productId', String(options.productId));
    if (options?.propModelId) formData.append('propModelId', options.propModelId);
    if (options?.subfolder) formData.append('subfolder', options.subfolder);

    return api.post<UploadResponse>('/upload/3d-model', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        const percent = Math.round((e.loaded / (e.total || file.size)) * 100);
        onProgress?.(percent, e.loaded, e.total || file.size);
      },
    });
  },

  // Upload image file
  uploadImage: (
    file: File,
    options?: { productId?: number; propModelId?: string; subfolder?: string; name?: string },
    onProgress?: (percent: number, loaded: number, total: number) => void
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.productId) formData.append('productId', String(options.productId));
    if (options?.propModelId) formData.append('propModelId', options.propModelId);
    if (options?.subfolder) formData.append('subfolder', options.subfolder);
    if (options?.name) formData.append('name', options.name);

    return api.post<UploadResponse>('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        const percent = Math.round((e.loaded / (e.total || file.size)) * 100);
        onProgress?.(percent, e.loaded, e.total || file.size);
      },
    });
  },

  // Upload PDF (design or production)
  uploadPDF: (
    file: File,
    type: 'design' | 'production',
    options?: { productId?: number; subfolder?: string; name?: string },
    onProgress?: (percent: number, loaded: number, total: number) => void
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.productId) formData.append('productId', String(options.productId));
    if (options?.subfolder) formData.append('subfolder', options.subfolder);
    if (options?.name) formData.append('name', options.name);

    return api.post<UploadResponse>(`/upload/pdf/${type}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        const percent = Math.round((e.loaded / (e.total || file.size)) * 100);
        onProgress?.(percent, e.loaded, e.total || file.size);
      },
    });
  },

  // Upload generic file
  uploadFile: (
    file: File,
    options?: { targetPath?: string; productId?: number; groupCode?: string; name?: string },
    onProgress?: (percent: number, loaded: number, total: number) => void
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.targetPath) formData.append('targetPath', options.targetPath);
    if (options?.productId) formData.append('productId', String(options.productId));
    if (options?.groupCode) formData.append('groupCode', options.groupCode);
    if (options?.name) formData.append('name', options.name);

    return api.post<UploadResponse>('/upload/file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        const percent = Math.round((e.loaded / (e.total || file.size)) * 100);
        onProgress?.(percent, e.loaded, e.total || file.size);
      },
    });
  },
};

// =============================================================================
// PRODUCT GROUPS ENDPOINTS
// =============================================================================

export interface ProductGroup {
  id: number;
  name: string;
  texts: Record<string, string>;
  displayed: boolean;
  menu: number;
  productCount: number;
}

export const productGroupsApi = {
  list: (params?: { language?: string; showHidden?: boolean }) =>
    api.get<{ data: ProductGroup[] }>('/admin/products/groups/list', { params: { ...params, showHidden: params?.showHidden ? 'true' : undefined } }),
  
  create: (data: {
    texts: Record<string, string>;
    displayed?: boolean;
    menu?: number;
  }) => api.post<{ message: string; group: { id: number } }>('/admin/products/groups', data),
  
  update: (groupId: number, data: {
    texts?: Record<string, string>;
    displayed?: boolean;
    menu?: number;
  }) => api.put<{ message: string }>(`/admin/products/groups/${groupId}`, data),
  
  delete: (groupId: number) =>
    api.delete<{ message: string }>(`/admin/products/groups/${groupId}`),
  
  toggleDisplayed: (groupId: number) =>
    api.post<{ id: number; displayed: boolean }>(`/admin/products/groups/${groupId}/toggle-displayed`),
};

// =============================================================================
// PRODUCT PROPERTIES ENDPOINTS
// =============================================================================

export interface ProductProperty {
  id: number;
  name: string;
  isEnabled: boolean;
  link: string | null;
  sortOrder: number;
}

export const productPropertiesApi = {
  list: (showDisabled?: boolean) =>
    api.get<{ data: ProductProperty[] }>('/admin/products/properties/list', { params: { showDisabled: showDisabled ? 'true' : undefined } }),
  
  create: (data: {
    name: string;
    link?: string;
    sortOrder?: number;
  }) => api.post<{ message: string; property: { id: number; name: string } }>('/admin/products/properties', data),
  
  update: (propertyId: number, data: {
    name?: string;
    link?: string;
    sortOrder?: number;
    isEnabled?: boolean;
  }) => api.put<{ message: string }>(`/admin/products/properties/${propertyId}`, data),
  
  delete: (propertyId: number) =>
    api.delete<{ message: string }>(`/admin/products/properties/${propertyId}`),
  
  toggleEnabled: (propertyId: number) =>
    api.post<{ id: number; isEnabled: boolean }>(`/admin/products/properties/${propertyId}/toggle-enabled`),
};

// Keep old groupsApi for compatibility
export const groupsApi = productGroupsApi;

// =============================================================================
// ROLES ENDPOINTS
// =============================================================================

export interface RoleInfo {
  id: number;
  name: string;
}

export const rolesApi = {
  list: () => api.get<{ roles: RoleInfo[] }>('/admin/roles'),
  
  create: (name: string) =>
    api.post<{ message: string; role: RoleInfo }>('/admin/roles', { name }),
  
  update: (id: number, name: string) =>
    api.put<{ message: string; role: RoleInfo }>(`/admin/roles/${id}`, { name }),
  
  delete: (id: number) =>
    api.delete<{ message: string }>(`/admin/roles/${id}`),
};

// =============================================================================
// USERS ENDPOINTS
// Uses email (string) as identifier
// =============================================================================

export const usersApi = {
  list: (params?: UserFilters & { page?: number; limit?: number; role?: string }) =>
    api.get<PaginatedResponse<User>>('/admin/users', { params }),

  create: (data: { email: string; name?: string; password: string; roleIds: number[] }) =>
    api.post<{ message: string; email: string }>('/admin/users', data),

  get: (email: string) =>
    api.get<User>(`/admin/users/${encodeURIComponent(email)}`),
  
  update: (email: string, data: {
    userName?: string;
    phoneNumber?: string;
    city?: string;
    address?: string;
    postCode?: string;
    postOffice?: string;
    country?: string;
    language?: string;
    currency?: string;
    isActive?: boolean;
    roleIds?: number[];
  }) => api.patch<User>(`/admin/users/${encodeURIComponent(email)}`, data),
  
  updateRoles: (email: string, roleIds: number[]) =>
    api.patch<{ message: string; roles: RoleInfo[] }>(`/admin/users/${encodeURIComponent(email)}/roles`, { roleIds }),
  
  toggleActive: (email: string) =>
    api.post<{ email: string; isActive: boolean }>(`/admin/users/${encodeURIComponent(email)}/toggle-active`),
};

// =============================================================================
// SITE CONFIGURATION ENDPOINTS (Admin only)
// =============================================================================

export interface SiteConfig {
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string;
  /** Write-only: the API sends it back empty, and saving it empty keeps the stored one. */
  smtpPassword: string;
  /** True when a password is stored. */
  smtpPasswordSet?: boolean;
  smtpFromEmail: string;
  smtpFromName: string;
  siteUrl: string;
}

export const siteConfigApi = {
  get: () => api.get<SiteConfig>('/admin/site-config'),
  
  update: (config: Partial<SiteConfig>) => 
    api.patch<{ message: string }>('/admin/site-config', config),
  
  testSmtp: (config: { smtpHost: string; smtpPort: number; smtpSecure: boolean; smtpUser: string; smtpPassword: string }) =>
    api.post<{ success: boolean; message?: string; error?: string }>('/admin/site-config/test-smtp', config),
};

// =============================================================================
// LANGUAGE SETTINGS ENDPOINTS (Admin only - for frontend website)
// =============================================================================

export interface LanguageInfo {
  code: string;
  name: string;
  flag: string;
}

export interface LanguageSettings {
  allLanguages: LanguageInfo[];
  enabledLanguages: string[];
  defaultLanguage: string;
}

export const languageSettingsApi = {
  get: () => api.get<LanguageSettings>('/admin/language-settings'),
  
  update: (settings: { enabledLanguages?: string[]; defaultLanguage?: string }) =>
    api.patch<{ message: string }>('/admin/language-settings', settings),
};

// =============================================================================
// CURRENCY SETTINGS ENDPOINTS (Admin only - for frontend website)
// =============================================================================

export interface CurrencyInfo {
  code: string;
  name: string;
  symbol: string;
  rateToEuro: number;
  decimalDigits: number;
  isSymbolBefore: boolean;
  isActive: boolean;
  isDefault: boolean;
}

export interface CurrencySettings {
  currencies: CurrencyInfo[];
}

export const currencySettingsApi = {
  get: () => api.get<CurrencySettings>('/admin/currency-settings'),
  
  update: (currencies: CurrencyInfo[]) =>
    api.patch<{ message: string }>('/admin/currency-settings', { currencies }),
  
  updateSingle: (code: string, data: Partial<CurrencyInfo>) =>
    api.patch<{ message: string }>(`/admin/currency-settings/${code}`, data),
  
  create: (data: {
    code: string;
    name: string;
    symbol?: string;
    rateToEuro?: number;
    decimalDigits?: number;
    isSymbolBefore?: boolean;
  }) => api.post<{ message: string; currency: CurrencyInfo }>('/admin/currencies', data),
  
  updateFull: (code: string, data: Partial<CurrencyInfo>) =>
    api.put<{ message: string; currency: CurrencyInfo }>(`/admin/currencies/${code}`, data),
  
  delete: (code: string) =>
    api.delete<{ message: string }>(`/admin/currencies/${code}`),
};

// =============================================================================
// FILE TYPES ENDPOINTS (Admin only - for production file management)
// =============================================================================

export interface FileTypeName {
  id?: string;
  languageId: string;
  name: string;
}

export interface FileTypeInfo {
  id: string;
  code: string;
  path: string | null;
  sortOrder: number;
  names: FileTypeName[];
}

export const fileTypesApi = {
  list: () => api.get<{ fileTypes: FileTypeInfo[] }>('/admin/file-types'),
  
  get: (id: string) => api.get<FileTypeInfo>(`/admin/file-types/${id}`),
  
  create: (data: {
    code: string;
    path?: string;
    sortOrder?: number;
    names?: FileTypeName[];
  }) => api.post<{ message: string; fileType: FileTypeInfo }>('/admin/file-types', data),
  
  update: (id: string, data: {
    code?: string;
    path?: string;
    sortOrder?: number;
    names?: FileTypeName[];
  }) => api.put<{ message: string; fileType: FileTypeInfo }>(`/admin/file-types/${id}`, data),
  
  delete: (id: string) =>
    api.delete<{ message: string }>(`/admin/file-types/${id}`),
};

// =============================================================================
// RESELLER (ACCOUNT) ENDPOINTS
// =============================================================================

export interface ResellerContactPerson {
  id?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  title?: string;
  phone?: string;
  email?: string;
}

export interface Reseller {
  id: number;
  name: string | null;
  vatId: string | null;
  businessId?: string | null;
  address: string | null;
  postCode: string | null;
  city: string | null;
  country: string | null;
  countryCode?: string | null;
  phone: string | null;
  mobilePhone?: string | null;
  website: string | null;
  email: string | null;
  notes: string | null;
  isActive: boolean;
  contactPerson: ResellerContactPerson | null;
  userCount?: number;
  contacts?: ResellerContactPerson[];
  products?: { id: number; name: string }[];
}

export interface ResellerFilters {
  search?: string;
  isActive?: boolean | string;
}

export const resellersApi = {
  list: (params?: ResellerFilters & { page?: number; limit?: number }) =>
    api.get<PaginatedResponse<Reseller>>('/admin/resellers', { params }),
  
  get: (id: number) =>
    api.get<Reseller>(`/admin/resellers/${id}`),
  
  create: (data: {
    name: string;
    email: string;
    vatId?: string;
    businessId?: string;
    address?: string;
    postCode?: string;
    city?: string;
    country?: string;
    countryCode?: string;
    phone?: string;
    mobilePhone?: string;
    website?: string;
    notes?: string;
    contactPerson?: ResellerContactPerson;
  }) => api.post<{ message: string; reseller: { id: number; name: string } }>('/admin/resellers', data),
  
  update: (id: number, data: Partial<Reseller> & { contactPerson?: ResellerContactPerson }) =>
    api.put<{ message: string }>(`/admin/resellers/${id}`, data),
  
  delete: (id: number) =>
    api.delete<{ message: string }>(`/admin/resellers/${id}`),
  
  toggleActive: (id: number) =>
    api.post<{ id: number; isActive: boolean }>(`/admin/resellers/${id}/toggle-active`),
  
  // Product assignment
  getProducts: (id: number) =>
    api.get<{ products: { id: number; name: string; code: string }[] }>(`/admin/resellers/${id}/products`),
  
  assignProducts: (id: number, productIds: number[]) =>
    api.post<{ message: string }>(`/admin/resellers/${id}/products`, { productIds }),
  
  // 3D Props assignment
  getProps: (id: number) =>
    api.get<{ props: { id: string; name: string; category: string | null; thumbnailPath: string | null }[] }>(`/admin/resellers/${id}/props`),
  
  assignProps: (id: number, propIds: string[]) =>
    api.post<{ message: string }>(`/admin/resellers/${id}/props`, { propIds }),
};

// =============================================================================
// END USER ENDPOINTS
// =============================================================================

export interface EndUser {
  email: string;
  name: string | null;
  company: string | null;
  vatId: string | null;
  phone: string | null;
  address: string | null;
  postCode: string | null;
  city: string | null;
  country: string | null;
  isActive: boolean;
  isVerified: boolean;
  created: string;
  lastLogin: string | null;
  account: { id: number; name: string } | null;
  roles?: { id: number; name: string }[];
}

export interface EndUserFilters {
  search?: string;
  isActive?: boolean | string;
  accountId?: number;
}

export const endUsersApi = {
  list: (params?: EndUserFilters & { page?: number; limit?: number }) =>
    api.get<PaginatedResponse<EndUser>>('/admin/end-users', { params }),
  
  get: (email: string) =>
    api.get<EndUser>(`/admin/end-users/${encodeURIComponent(email)}`),
  
  create: (data: {
    email: string;
    name?: string;
    company?: string;
    vatId?: string;
    phone?: string;
    address?: string;
    postCode?: string;
    city?: string;
    country?: string;
    password?: string;
    accountId?: number;
  }) => api.post<{ message: string; user: { email: string; name: string } }>('/admin/end-users', data),
  
  update: (email: string, data: Partial<EndUser> & { password?: string }) =>
    api.put<{ message: string }>(`/admin/end-users/${encodeURIComponent(email)}`, data),
  
  delete: (email: string) =>
    api.delete<{ message: string }>(`/admin/end-users/${encodeURIComponent(email)}`),
  
  toggleActive: (email: string) =>
    api.post<{ email: string; isActive: boolean }>(`/admin/end-users/${encodeURIComponent(email)}/toggle-active`),
  
  verify: (email: string) =>
    api.post<{ message: string }>(`/admin/end-users/${encodeURIComponent(email)}/verify`),
  
  resendVerification: (email: string) =>
    api.post<{ message: string }>(`/admin/end-users/${encodeURIComponent(email)}/resend-verification`),
};

// =============================================================================
// EMPLOYEE ENDPOINTS
// =============================================================================

export interface Employee {
  id: number;
  firstName: string;
  lastName: string;
  name: string;
  address: string | null;
  postCode: string | null;
  city: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  hourlySalary: number | null;
  workHoursPerformed: number;
  isSalesPerson: boolean;
  isActive: boolean;
  created: string;
  updated: string;
}

export interface EmployeeFilters {
  search?: string;
  isActive?: boolean | string;
  isSalesPerson?: boolean | string;
}

export const employeesApi = {
  list: (params?: EmployeeFilters & { page?: number; limit?: number }) =>
    api.get<PaginatedResponse<Employee>>('/employees', { params }),
  
  get: (id: number) =>
    api.get<Employee>(`/employees/${id}`),
  
  create: (data: {
    firstName: string;
    lastName: string;
    address?: string;
    postCode?: string;
    city?: string;
    country?: string;
    phone?: string;
    email?: string;
    hourlySalary?: number;
    isSalesPerson?: boolean;
  }) => api.post<{ message: string; employee: { id: number; name: string } }>('/employees', data),
  
  update: (id: number, data: Partial<Employee>) =>
    api.put<{ message: string }>(`/employees/${id}`, data),
  
  delete: (id: number) =>
    api.delete<{ message: string }>(`/employees/${id}`),
  
  toggleActive: (id: number) =>
    api.post<{ id: number; isActive: boolean }>(`/employees/${id}/toggle-active`),
  
  getSalesPersons: () =>
    api.get<{ data: { id: number; name: string; email: string | null }[] }>('/employees/sales-persons/list'),
};

// =============================================================================
// SELLER ENDPOINTS
// =============================================================================

export interface Seller {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  isActive: boolean;
  created: string;
  accountCount: number;
  account: { id: number; name: string } | null;
}

export interface SellerFilters {
  search?: string;
  isActive?: boolean | string;
}

export interface SellerAccountAssignment {
  id: string;
  seller: {
    id: string;
    name: string | null;
    email: string | null;
  } | null;
  account: {
    id: number;
    name: string | null;
    company: string | null;
  } | null;
  created: string;
}

export interface AssignmentFilters {
  sellerId?: string;
  search?: string;
}

export const sellersApi = {
  list: (params?: SellerFilters & { page?: number; limit?: number }) =>
    api.get<PaginatedResponse<Seller>>('/sellers', { params }),

  get: (id: string) =>
    api.get<Seller>(`/sellers/${id}`),

  update: (id: string, data: { name?: string; email?: string; phone?: string; isActive?: boolean }) =>
    api.put<{ message: string }>(`/sellers/${id}`, data),

  toggleActive: (id: string) =>
    api.post<{ id: string; isActive: boolean }>(`/sellers/${id}/toggle-active`),

  // Seller Account Assignments
  listAssignments: (params?: AssignmentFilters & { page?: number; limit?: number }) =>
    api.get<PaginatedResponse<SellerAccountAssignment>>('/sellers/assignments', { params }),

  getAvailableSellers: () =>
    api.get<{ data: { id: string; name: string | null; email: string | null }[] }>('/sellers/available-sellers'),

  getAvailableAccounts: (sellerId?: string, search?: string) =>
    api.get<{ data: { id: number; name: string | null; company: string | null }[] }>('/sellers/available-accounts', { params: { sellerId, search } }),

  createAssignment: (data: { sellerId: string; accountId: number }) =>
    api.post<{ message: string; assignment: { id: string } }>('/sellers/assignments', data),

  deleteAssignment: (id: string) =>
    api.delete<{ message: string }>(`/sellers/assignments/${id}`),
};

// =============================================================================
// RESOURCE ENDPOINTS
// =============================================================================

export interface Resource {
  id: number;
  name: string;
  description: string | null;
  costPerHour: number | null;
  capacityHoursDay: number | null;
  capacityUnit: string | null;
  costPerUnit: number | null;
  unitsPerHour: number | null;
  salesPricePerUnit: number | null;
  notes: string | null;
  isActive: boolean;
  created: string;
  updated: string;
}

export interface ResourceFilters {
  search?: string;
  isActive?: boolean | string;
}

export const resourcesApi = {
  list: (params?: ResourceFilters & { page?: number; limit?: number }) =>
    api.get<PaginatedResponse<Resource>>('/resources', { params }),
  
  get: (id: number) =>
    api.get<Resource>(`/resources/${id}`),
  
  create: (data: {
    name: string;
    description?: string;
    costPerHour?: number;
    capacityHoursDay?: number;
    capacityUnit?: string;
    costPerUnit?: number;
    unitsPerHour?: number;
    salesPricePerUnit?: number;
    notes?: string;
  }) => api.post<{ message: string; resource: { id: number; name: string } }>('/resources', data),
  
  update: (id: number, data: Partial<Resource>) =>
    api.put<{ message: string }>(`/resources/${id}`, data),
  
  delete: (id: number) =>
    api.delete<{ message: string }>(`/resources/${id}`),
  
  toggleActive: (id: number) =>
    api.post<{ id: number; isActive: boolean }>(`/resources/${id}/toggle-active`),
  
  getActiveList: () =>
    api.get<{ data: { id: number; name: string; capacityUnit: string | null; costPerUnit: number | null; salesPricePerUnit: number | null }[] }>('/resources/active/list'),
};

// =============================================================================
// MATERIAL ENDPOINTS
// =============================================================================

export interface Material {
  id: number;
  code: string;
  name: string;
  purchasePrice: number | null;
  salesPrice: number | null;
  purchaseUnit: string | null;
  manufacturingUnit: string | null;
  manufacturingFactor: number | null;
  width: number | null;
  height: number | null;
  isActive: boolean;
  created: string;
  updated: string;
}

export interface MaterialFilters {
  search?: string;
  isActive?: boolean | string;
}

export const materialsApi = {
  list: (params?: MaterialFilters & { page?: number; limit?: number }) =>
    api.get<PaginatedResponse<Material>>('/materials', { params }),
  
  get: (id: number) =>
    api.get<Material>(`/materials/${id}`),
  
  create: (data: {
    code: string;
    name: string;
    purchasePrice?: number;
    salesPrice?: number;
    purchaseUnit?: string;
    manufacturingUnit?: string;
    manufacturingFactor?: number;
    width?: number;
    height?: number;
  }) => api.post<{ message: string; material: { id: number; code: string; name: string } }>('/materials', data),
  
  update: (id: number, data: Partial<Material>) =>
    api.put<{ message: string }>(`/materials/${id}`, data),
  
  delete: (id: number) =>
    api.delete<{ message: string }>(`/materials/${id}`),
  
  toggleActive: (id: number) =>
    api.post<{ id: number; isActive: boolean }>(`/materials/${id}/toggle-active`),
  
  getActiveList: () =>
    api.get<{ data: { id: number; code: string; name: string; salesPrice: number | null; manufacturingUnit: string | null }[] }>('/materials/active/list'),
};

// =============================================================================
// PROP MODELS (3D PROPS) ENDPOINTS
// =============================================================================

export interface PropModelImage {
  id: string;
  name: string | null;
  filePath: string | null;
  imageData: string | null;
  mimeType: string | null;
  isSnapshot: boolean;
  sortOrder: number;
}

export interface PropModel {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  tags: string[];
  modelFilePath: string | null;
  modelFileType: string | null;
  isVisible: boolean;
  isOwn: boolean;
  thumbnailData: string | null;
  thumbnailPath: string | null;
  imageCount: number;
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number };
  scale: number;
  images: PropModelImage[];
  assignedAccounts: { accountId: number; accountName: string }[];
  createdAt: string;
  updatedAt?: string;
}

export interface PropModelFilters {
  search?: string;
  category?: string;
  showHidden?: boolean;
}

export const propModelsApi = {
  list: (params?: PropModelFilters & { page?: number; limit?: number }) =>
    api.get<PaginatedResponse<PropModel> & { categories: string[] }>('/prop-models', { params }),
  
  get: (id: string) =>
    api.get<PropModel>(`/prop-models/${id}`),
  
  create: (data: {
    name: string;
    description?: string;
    category?: string;
    tags?: string[];
    modelFilePath?: string;
    modelFileType?: string;
    isVisible?: boolean;
    position?: { x: number; y: number; z: number };
    rotation?: { x: number; y: number; z: number };
    scale?: number;
  }) => api.post<{ message: string; model: { id: string; name: string } }>('/prop-models', data),
  
  update: (id: string, data: Partial<{
    name: string;
    description: string;
    category: string;
    tags: string[];
    modelFilePath: string;
    modelFileType: string;
    isVisible: boolean;
    position: { x: number; y: number; z: number };
    rotation: { x: number; y: number; z: number };
    scale: number;
  }>) => api.put<{ message: string }>(`/prop-models/${id}`, data),
  
  delete: (id: string) =>
    api.delete<{ message: string }>(`/prop-models/${id}`),
  
  // Account assignment (main company only)
  assign: (id: string, accountIds: number[]) =>
    api.post<{ message: string }>(`/prop-models/${id}/assign`, { accountIds }),
  
  // Get accounts for assignment
  getAccounts: () =>
    api.get<{ accounts: { id: number; name: string }[] }>('/prop-models/accounts/list'),
  
  // Images
  addImage: (modelId: string, data: {
    name?: string;
    filePath?: string;
    imageData?: string;
    mimeType?: string;
    isSnapshot?: boolean;
  }) => api.post<{ message: string; image: { id: string; name: string; isSnapshot: boolean } }>(`/prop-models/${modelId}/images`, data),
  
  updateImage: (modelId: string, imageId: string, data: { name?: string; sortOrder?: number }) =>
    api.patch<{ message: string }>(`/prop-models/${modelId}/images/${imageId}`, data),
  
  deleteImage: (modelId: string, imageId: string) =>
    api.delete<{ message: string }>(`/prop-models/${modelId}/images/${imageId}`),
};

// =============================================================================
// SCENE API (3D Configurator)
// =============================================================================

export interface SceneProductFile {
  id: string;
  fileName: string;
  groupCode: string;
  topBleed?: number;
  bottomBleed?: number;
  leftBleed?: number;
  rightBleed?: number;
  isBraid?: boolean;
  horizontalOffset?: number;
  verticalOffset?: number;
  meshCount?: number;
  meshWidth?: number;
  meshHeight?: number;
}

export interface SceneProduct {
  id: number;
  type: string;
  code: string;
  position?: string;
  model3DPath?: string;
  babylonjs?: string;
  edgeColor?: string;
  pdfWidth?: number;
  pdfHeight?: number;
  moveXup?: number;
  moveXdown?: number;
  moveZup?: number;
  moveZdown?: number;
  rotationYup?: number;
  rotationYdown?: number;
  productFiles: SceneProductFile[];
  subProducts?: {
    id: number;
    isMain: boolean;
    quantity: number;
    sortOrder: number;
    product: SceneProduct | null;
  }[];
}

export interface SceneOwnProduct {
  id: number;
  code: string;
  name: string;
  edgeColor?: string;
  width?: number;
  height?: number;
  position?: string;
  product: SceneProduct | null;
  files: { id: string; fileType: string; fileName: string }[];
}

export interface SceneOrderProduct {
  id: number;
  count: number;
  position?: string;
  width?: number;
  height?: number;
  product: SceneProduct | null;
  subProducts: {
    id: string;
    subProductId: number;
    isMain: boolean;
    position?: string;
    amount: number;
  }[];
  files: { id: string; fileType: string; fileName: string }[];
}

export interface SceneOrderOwnProduct {
  id: number;
  count: number;
  position?: string;
  width?: number;
  height?: number;
  ownProduct: SceneOwnProduct | null;
  files: { id: string; fileType: string; fileName: string }[];
}

export interface SceneData {
  order: {
    id: number;
    code: string;
    name?: string;
    amount: number;
    isActive: boolean;
    currencyId?: string;
    groupItems?: any;
    groupOwnItems?: any;
  };
  orderProducts: SceneOrderProduct[];
  orderOwnProducts: SceneOrderOwnProduct[];
  siteParameters: {
    textureDpiLow: number;
    textureDpi: number;
    lightIntensity: number;
    ambientColor: string;
    skyboxPath?: string;
  };
  user?: {
    email: string;
    name?: string;
  };
}

export interface SubProductInfo {
  id: number;
  isMain: boolean;
  quantity: number;
  sortOrder: number;
  product: {
    id: number;
    type: string;
    code: string;
    name: string;
    description?: string;
    groupName?: string;
    position?: string;
    model3DPath?: string;
    pdfWidth?: number;
    pdfHeight?: number;
    price: number;
    moveXup?: number;
    moveXdown?: number;
    moveZup?: number;
    moveZdown?: number;
    rotationYup?: number;
    rotationYdown?: number;
  };
}

export const sceneApi = {
  // Load scene by order code
  getScene: (code: string, shareCode?: string) =>
    api.get<SceneData>(`/scene/${code}`, { params: shareCode ? { share: shareCode } : {} }),

  // Get current user's active order
  getActiveOrder: () =>
    api.get<{ OrderID: number; Code: string; OrderName?: string; Amount: number }>('/scene/current/active'),

  // Save scene (product positions)
  saveScene: (code: string, data: {
    orderProducts?: { id: number; position: string; subProducts?: { id: string; position: string }[] }[];
    orderOwnProducts?: { id: number; position: string }[];
    groupItems?: any;
    groupOwnItems?: any;
  }) => api.post<{ message: string }>(`/scene/${code}/save`, data),

  // Add product to scene
  addProduct: (orderCode: string, productCode: string, data?: { count?: number; position?: string }) =>
    api.post<any>(`/scene/${orderCode}/products/${productCode}`, data || {}),

  // Create own product (custom with user images)
  createOwnProduct: (data: {
    productCode: string;
    name: string;
    description?: string;
    width?: number;
    height?: number;
    orderCode?: string;
  }) => api.post<{ id: number; code: string; name: string; productType: string }>('/scene/own-products', data),

  // Upload design PDF (converts to texture)
  uploadDesign: (
    ownProductCode: string,
    file: File,
    surfaceName?: string,
    onProgress?: (percent: number) => void
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    if (surfaceName) formData.append('surfaceName', surfaceName);
    
    return api.post<{ success: boolean; pdfPath: string; texturePath: string; surfaceName: string }>(
      `/scene/own-products/${ownProductCode}/design`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => onProgress?.(Math.round((e.loaded / (e.total || 1)) * 100)),
      }
    );
  },

  // Upload texture image directly
  uploadTexture: (
    ownProductCode: string,
    file: File,
    surfaceName?: string,
    onProgress?: (percent: number) => void
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    if (surfaceName) formData.append('surfaceName', surfaceName);
    
    return api.post<{ success: boolean; texturePath: string; surfaceName: string }>(
      `/scene/own-products/${ownProductCode}/texture`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => onProgress?.(Math.round((e.loaded / (e.total || 1)) * 100)),
      }
    );
  },

  // Update own product (edge color, dimensions)
  updateOwnProduct: (code: string, data: { edgeColor?: string; width?: number; height?: number; name?: string }) =>
    api.patch<{ id: number; code: string; edgeColor?: string }>(`/scene/own-products/${code}`, data),

  // Share scene
  shareScene: (code: string, data: {
    email?: string;
    title?: string;
    message?: string;
    showPrice?: boolean;
    showCart?: boolean;
  }) => api.post<{ shareCode: string; shareUrl: string }>(`/scene/${code}/share`, data),

  // Save screenshot
  saveScreenshot: (code: string, imageBlob: Blob, onProgress?: (percent: number) => void) => {
    const formData = new FormData();
    formData.append('image', imageBlob, 'screenshot.jpg');
    
    return api.post<{ success: boolean; path: string }>(
      `/scene/${code}/screenshot`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => onProgress?.(Math.round((e.loaded / (e.total || 1)) * 100)),
      }
    );
  },

  // Get available sub-products for a group product
  getSubProducts: (productId: number, language?: string) =>
    api.get<SubProductInfo[]>(`/scene/products/${productId}/subproducts`, { params: { language } }),
};

// =============================================================================
// SHOPPING CARTS API
// =============================================================================

export interface ShoppingCartListItem {
  id: number;
  code: string;
  name: string | null;
  amount: number;
  currencyId: string | null;
  created: Date | string | null;
  updated: Date | string | null;
  ordered: Date | string | null;
  shared: Date | string | null;
  isActive: boolean;
  userEmail: string | null;
  userName: string | null;
  accountId: number | null;
  accountName: string | null;
  resellerAccountId: number | null;
  resellerAccountName: string | null;
  userAccountNames?: string[];
  productCount: number;
  shareCount: number;
}

export interface ShoppingCartsResponse {
  items: ShoppingCartListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ResellerFilter {
  resellers: { AccountID: number; Company: string }[];
  mainAccounts: { AccountID: number; Company: string }[];
}

export interface TabCounts {
  open: number;
  shared: number;
  ordered: number;
}


// Shopping Cart Detail interface
export interface CartDetail {
  order: {
    OrderID: number;
    OrderName: string | null;
    Code: string;
    Status: string | null;
    Amount: number;
    CurrencyId: string | null;
    Created: string | null;
    Updated: string | null;
    Ordered: string | null;
    IsActive: boolean;
  };
  user: {
    UserName: string | null;
    UserEmailAddress: string;
    PhoneNumber: string | null;
    Address: string | null;
    PostCode: string | null;
    PostOffice: string | null;
  } | null;
  accounts: Array<{
    AccountID: number;
    Company: string | null;
    AccountName: string | null;
    Address: string | null;
    PostCode: string | null;
    PostOffice: string | null;
    YTunnus: string | null;
  }>;
  currency: { Symbol: string | null } | null;
  orderProducts: Array<{
    OrderProductID: number;
    Count: number;
    SellPrice: number | null;
    TotalPrice: number | null;
    CurrencyId: string | null;
    productName: string | null;
    files: string[];
    subProducts: Array<{
      name: string | null;
      quantity: number;
      sellPrice: number | null;
      totalPrice: number | null;
      currencyId: string | null;
    }>;
  }>;
  orderOwnProducts: Array<{
    OrderOwnProductID: number;
    Count: number;
    SellPrice: number | null;
    TotalPrice: number | null;
    CurrencyId: string | null;
    ownProductName: string | null;
    productName: string | null;
    ownProductCode: string | null;
    files: string[];
    subProducts: Array<{
      name: string | null;
      quantity: number;
      sellPrice: number | null;
      totalPrice: number | null;
      currencyId: string | null;
    }>;
  }>;
  orderShares: Array<{
    OrderShareId: number;
    Created: string | null;
    From: string | null;
    To: string | null;
    Title: string | null;
    Message: string | null;
  }>;
  messages: Array<{
    MessageId: number;
    Created: string | null;
    MessageContent: string | null;
  }>;
}

export const shoppingCartsApi = {
  // List shopping carts with filtering
  list: (params: {
    tab?: 'open' | 'shared' | 'ordered';
    accountId?: number;
    resellerAccountId?: string;
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => api.get<ShoppingCartsResponse>('/shopping-carts', { params }),

  // Get single cart details
  get: (id: number) => api.get<any>(`/shopping-carts/${id}`),

  // Update cart
  update: (id: number, data: {
    orderName?: string;
    customerReference?: string;
    isActive?: boolean;
    accountId?: number;
  }) => api.patch<any>(`/shopping-carts/${id}`, data),

  // Delete cart
  delete: (id: number) => api.delete<{ message: string }>(`/shopping-carts/${id}`),

  // Get tab counts for badges
  getTabCounts: (resellerAccountId?: string) =>
    api.get<TabCounts>('/shopping-carts/counts/tabs', { 
      params: resellerAccountId ? { resellerAccountId } : {} 
    }),

  // Get reseller filters
  getResellerFilters: () => api.get<ResellerFilter>('/shopping-carts/filters/resellers'),

  // Get full cart detail (products, shares, messages)
  getDetail: (id: number, lang?: string) => 
    api.get<CartDetail>(`/shopping-carts/${id}/detail`, { 
      params: lang ? { lang } : {} 
    }),
};

// =============================================================================
// SALES OFFERS / ORDER BACKLOG API
// =============================================================================

export type SalesOfferStatus = 'CREATED' | 'SENT' | 'ACCEPTED' | 'INVOICED';

export interface SalesOfferListItem {
  id: string;
  offerNumber: number;
  name: string | null;
  status: SalesOfferStatus;
  sellerEmail: string;
  sellerName: string | null;
  customerEmail: string | null;
  customerName: string | null;
  customerAccountId: number | null;
  customerAccountName: string | null;
  company: string | null;
  deliveryCompany: string | null;
  contactName: string | null;
  contactEmail: string | null;
  lineCount: number;
  totalAmount: number;
  finalAmount: number;
  currencyId: string;
  sceneSnapshotId: string | null;
  offerDate: Date | string | null;
  createdAt: Date | string;
  sentAt: Date | string | null;
  acceptedAt: Date | string | null;
  invoicedAt: Date | string | null;
  isPlanned: boolean;
}

export interface SalesOffersResponse {
  items: SalesOfferListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SellerFilter {
  UserEmailAddress: string;
  UserName: string | null;
}

export interface CustomerFilter {
  AccountID: number;
  Company: string | null;
  AccountName: string | null;
}

export interface StatusCounts {
  created: number;
  offered: number;
  notPlanned: number;
  planned: number;
  total: number;
}

export const salesOffersApi = {
  // List sales offers
  list: (params: {
    accountId?: number;
    status?: SalesOfferStatus;
    sellerEmail?: string;
    customerAccountId?: number;
    company?: string;
    planned?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => api.get<SalesOffersResponse>('/sales-offers', { params }),

  // Get single offer
  get: (id: string) => api.get<any>(`/sales-offers/${id}`),

  // Get status counts
  getCounts: (accountId?: number) =>
    api.get<StatusCounts>('/sales-offers/counts', { params: accountId ? { accountId } : {} }),

  // Create offer
  create: (data: {
    name?: string;
    description?: string;
    customerEmail?: string;
    customerAccountId?: number;
    accountId?: number;
    currencyId?: string;
    validUntil?: string;
    internalNotes?: string;
    customerNotes?: string;
    lines?: any[];
    sceneSnapshotId?: string;
  }) => api.post<any>('/sales-offers', data),

  // Update offer
  update: (id: string, data: any) => api.patch<any>(`/sales-offers/${id}`, data),

  // Update status
  updateStatus: (id: string, status: SalesOfferStatus) =>
    api.patch<any>(`/sales-offers/${id}/status`, { status }),

  // Delete offer
  delete: (id: string) => api.delete<{ message: string }>(`/sales-offers/${id}`),

  // Line management
  addLine: (offerId: string, data: any) =>
    api.post<any>(`/sales-offers/${offerId}/lines`, data),
  
  updateLine: (offerId: string, lineId: string, data: any) =>
    api.patch<any>(`/sales-offers/${offerId}/lines/${lineId}`, data),
  
  deleteLine: (offerId: string, lineId: string) =>
    api.delete<{ message: string }>(`/sales-offers/${offerId}/lines/${lineId}`),

  // Copy as new offer
  copy: (id: string) => api.post<{ id: string; offerNumber: number }>(`/sales-offers/${id}/copy`),

  // Copy as sales order
  copyAsOrder: (id: string) => api.post<{ id: string; orderNumber: number }>(`/sales-offers/${id}/copy-as-order`),

  // Import from scene
  importFromScene: (offerId: string, sceneSnapshotId: string) =>
    api.post<{ message: string; lineCount: number }>(`/sales-offers/${offerId}/import-from-scene`, { sceneSnapshotId }),

  // Get filters
  getSellers: (accountId?: number) =>
    api.get<SellerFilter[]>('/sales-offers/filters/sellers', { params: accountId ? { accountId } : {} }),
  
  getCustomers: (sellerEmail?: string) =>
    api.get<CustomerFilter[]>('/sales-offers/filters/customers', { params: sellerEmail ? { sellerEmail } : {} }),
};

// =============================================================================
// SALES ORDERS API
// =============================================================================

export interface SalesOrderListItem {
  id: string;
  orderNumber: number;
  orderName: string | null;
  orderDate: Date | string | null;
  createdDate: Date | string;
  sellerEmail: string | null;
  sellerName: string | null;
  userName: string | null;
  email: string | null;
  company: string | null;
  invoiceCompany: string | null;
  deliveryCompany: string | null;
  deliveryMultipleAddresses: boolean;
  accountId: number | null;
  accountName: string | null;
  lineCount: number;
  totalPrice: number;
  currencyId: string;
  rateEuro: number;
  isPlanned: boolean;
  isProduced: boolean;
  isDelivered: boolean;
  isInvoiced: boolean;
}

export interface SalesOrdersResponse {
  items: SalesOrderListItem[];
  total: number;
  page: number;
  totalPages: number;
}

export interface SalesOrderStatusCounts {
  planned: number;
  produced: number;
  delivered: number;
  invoiced: number;
  total: number;
}

/** GET /sales-orders/:id/web - what the website stored with an order placed there. */
export interface SalesOrderWebInfo {
  invoice: { number: number; issueDate: string; dueDate: string; total: number; currency: string } | null;
  /** The 3D designer saved a picture of the design. */
  scene: boolean;
  /** Order lines that have print files the customer uploaded. */
  lines: {
    line: string;
    name: string;
    count: number;
    files: { id: string; name: string; size: number; type: string | null; uploadedAt: string }[];
  }[];
}

export interface SalesOrderLineItem {
  id: string;
  lineNumber: number | null;
  productName: string | null;
  amount: number | null;
  unitId: string | null;
  unitAbbr: string | null;
  sellPrice: number | null;
  additionalPrice: number | null;
  totalPrice: number | null;
  currencyId: string | null;
  discountPercent: number | null;
  invoicePrice: number | null;
  position: string | null;
  width: number | null;
  height: number | null;
  createdDate: string | null;
  isPlanned: boolean;
  isProduced: boolean;
  isDelivered: boolean;
  isInvoiced: boolean;
  additionalInfo: string | null;
  plannedDeliveryDate: string | null;
  deliveryDate: string | null;
}

export const salesOrdersApi = {
  list: (params: {
    accountId?: number;
    status?: string;
    sellerEmail?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    hasUninvoicedDelivered?: boolean;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => api.get<SalesOrdersResponse>('/sales-orders', { params }),

  get: (id: string) => api.get<any>(`/sales-orders/${id}`),

  getCounts: () => api.get<SalesOrderStatusCounts>('/sales-orders/counts'),

  updateStatus: (id: string, data: {
    isPlanned?: boolean;
    isProduced?: boolean;
    isDelivered?: boolean;
    isInvoiced?: boolean;
  }) => api.patch<any>(`/sales-orders/${id}/status`, data),

  update: (id: string, data: {
    orderName?: string;
    sellerEmail?: string;
    ordered?: string;
    description?: string;
    additionalInformation?: string;
    productionInstruction?: string;
    invoicingComment?: string;
    deliveryComment?: string;
    productionComment?: string;
    sellerComment?: string;
  }) => api.patch<any>(`/sales-orders/${id}`, data),

  updateAddress: (orderId: string, type: number, data: any) =>
    api.put<any>(`/sales-orders/${orderId}/addresses/${type}`, data),

  updateContact: (orderId: string, type: number, data: any) =>
    api.put<any>(`/sales-orders/${orderId}/contacts/${type}`, data),

  getLines: (id: string, lang?: string) =>
    api.get<SalesOrderLineItem[]>(`/sales-orders/${id}/lines`, { params: lang ? { lang } : undefined }),

  // Orders from the website: the invoice, the 3D design's picture and the
  // customer's print files (api routes/salesOrders.routes.ts, WEB SHOP).
  getWeb: (id: string, lang?: string) =>
    api.get<SalesOrderWebInfo>(`/sales-orders/${id}/web`, { params: lang ? { lang } : undefined }),
  downloadWebFile: (id: string, fileId: string) =>
    api.get<Blob>(`/sales-orders/${id}/web/files/${fileId}`, { responseType: 'blob' }),
  downloadWebInvoice: (id: string) =>
    api.get<Blob>(`/sales-orders/${id}/web/invoice`, { responseType: 'blob' }),
  getWebScene: (id: string) =>
    api.get<Blob>(`/sales-orders/${id}/web/scene.jpg`, { responseType: 'blob' }),

  // Toggle one or more per-line manufacturing flags. Server lazy-migrates the
  // line into the OrderLines table on the first edit of the order, then rolls
  // the per-line flags up to the order-level flags (BOOL_AND). Response
  // includes both the updated line and the rolled-up order flags.
  updateLineStatus: (orderId: string, lineId: string, data: {
    isPlanned?: boolean;
    isProduced?: boolean;
    isDelivered?: boolean;
    isInvoiced?: boolean;
  }) => api.patch<{
    id: string;
    isPlanned: boolean;
    isProduced: boolean;
    isDelivered: boolean;
    isInvoiced: boolean;
    order: {
      isPlanned: boolean;
      isProduced: boolean;
      isDelivered: boolean;
      isInvoiced: boolean;
    } | null;
  }>(`/sales-orders/${orderId}/lines/${lineId}/status`, data),

  // Update non-status editable fields on a line (auto-save on blur). Same
  // lazy-migration semantics as /status, no order rollup.
  updateLine: (orderId: string, lineId: string, data: {
    productName?: string | null;
    amount?: number | null;
    unitId?: string | null;
    width?: number | null;
    height?: number | null;
    discountPercent?: number | null;
    sellPrice?: number | null;
    invoicePrice?: number | null;
    additionalPrice?: number | null;
    plannedDeliveryDate?: string | null;
    deliveryDate?: string | null;
    additionalInfo?: string | null;
    lineNumber?: number | null;
  }) => api.patch<Partial<SalesOrderLineItem> & { id: string }>(`/sales-orders/${orderId}/lines/${lineId}`, data),

  // Units list for the per-line Unit dropdown.
  getUnits: (lang?: string) =>
    api.get<{ id: string; abbr: string | null; name: string | null }[]>(
      '/sales-orders/units',
      { params: lang ? { lang } : undefined },
    ),

  getAccountContacts: (accountId: number) =>
    api.get<any[]>(`/sales-orders/account-contacts/${accountId}`),

  delete: (id: string) => api.delete<{ success: boolean }>(`/sales-orders/${id}`),

  getSellers: () => api.get<SellerFilter[]>('/sales-orders/filters/sellers'),
  getCustomers: () => api.get<CustomerFilter[]>('/sales-orders/filters/customers'),
};

// =============================================================================
// SCENE SNAPSHOTS API
// =============================================================================

export interface SceneSnapshotItem {
  id: string;
  name: string | null;
  thumbnailPath: string | null;
  shareCode: string;
  shareUrl: string;
  isPublic: boolean;
  createdBy: string;
  createdAt: Date | string;
  viewCount: number;
}

export interface ScenePresetSlot {
  id: string;
  slot: number;
  name: string | null;
  thumbnailPath: string | null;
  savedAt: Date | string;
}

export const sceneSnapshotsApi = {
  // Create snapshot
  create: (data: {
    name?: string;
    description?: string;
    sceneData: any;
    thumbnailPath?: string;
    accountId?: number;
    isPublic?: boolean;
  }) => api.post<{ id: string; shareCode: string; shareUrl: string }>('/scene-management/snapshots', data),

  // Get snapshot by ID
  get: (id: string) => api.get<any>(`/scene-management/snapshots/${id}`),

  // Get by share code (public)
  getByShareCode: (shareCode: string) => api.get<any>(`/scene-management/shared/${shareCode}`),

  // List user's snapshots
  list: (params?: { accountId?: number; page?: number; limit?: number }) =>
    api.get<{ items: SceneSnapshotItem[]; total: number; page: number; limit: number; totalPages: number }>(
      '/scene-management/snapshots', { params }
    ),

  // Update snapshot
  update: (id: string, data: any) => api.patch<any>(`/scene-management/snapshots/${id}`, data),

  // Delete snapshot
  delete: (id: string) => api.delete<{ message: string }>(`/scene-management/snapshots/${id}`),

  // Order scene presets (quick select buttons 1-6, per order)
  // These store VARIATIONS of the same scene - positions, textures, camera
  getPresets: (orderCode: string) => 
    api.get<(ScenePresetSlot | null)[]>(`/scene-management/presets/${orderCode}`),
  
  savePreset: (orderCode: string, slot: number, data: { 
    name?: string; 
    variationData: {
      productPositions: Record<string, { x: number; y: number; z: number; rotationY: number }>;
      productTextures?: Record<string, { texturePath?: string; edgeColor?: string }>;
      camera: { alpha: number; beta: number; radius: number; target: { x: number; y: number; z: number } };
    }; 
    thumbnailPath?: string 
  }) => api.put<ScenePresetSlot>(`/scene-management/presets/${orderCode}/${slot}`, data),
  
  loadPreset: (orderCode: string, slot: number) => 
    api.get<{
      id: string;
      slot: number;
      name: string | null;
      variationData: {
        productPositions: Record<string, { x: number; y: number; z: number; rotationY: number }>;
        productTextures?: Record<string, { texturePath?: string; edgeColor?: string }>;
        camera: { alpha: number; beta: number; radius: number; target: { x: number; y: number; z: number } };
      };
      thumbnailPath: string | null;
      savedAt: Date | string;
    }>(`/scene-management/presets/${orderCode}/${slot}`),
  
  clearPreset: (orderCode: string, slot: number) => 
    api.delete<{ message: string }>(`/scene-management/presets/${orderCode}/${slot}`),
  
  renamePreset: (orderCode: string, slot: number, name: string) =>
    api.patch<ScenePresetSlot>(`/scene-management/presets/${orderCode}/${slot}/rename`, { name }),
};

// =============================================================================
// PRINT PROCESS API
// =============================================================================

export interface PrintTemplate {
  productId: number;
  productType: string;
  productName: string;
  ownProductCode?: string;
  files: {
    id: string;
    fileName: string;
    componentName: string;
    filePath: string;
    downloadUrl: string;
  }[];
}

export interface PrintTemplatesResponse {
  orderCode: string;
  templates: PrintTemplate[];
  instructions: {
    download: string;
    edit: string;
    naming: string;
    upload: string;
  };
}

export interface PrintUploadResult {
  fileName: string;
  componentName: string;
  texturePath: string;
  originalPdfPath: string;
  success: boolean;
  error?: string;
}

export interface PrintTexturesResponse {
  ownProductCode: string;
  productType: string;
  textures: { name: string; path: string; exists: boolean }[];
  expectedTextures: { templateFile: string; expectedTexture: string; lowTexture: string }[];
}

export interface PdfMappingResponse {
  productId: number;
  productType: string;
  pdfWidth: number;
  pdfHeight: number;
  mappings: {
    templateName: string;
    designName: string;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
  }[];
  rawMapping: string | null;
}

export const printApi = {
  // Get template PDFs for products in scene
  getTemplates: (orderCode: string) =>
    api.get<PrintTemplatesResponse>(`/print/templates/${orderCode}`),

  // Download single template PDF
  downloadTemplate: (fileId: string) =>
    api.get(`/print/download/${fileId}`, { responseType: 'blob' }),

  // Download all templates as ZIP
  downloadAllTemplates: (orderCode: string) =>
    api.get(`/print/download-all/${orderCode}`, { responseType: 'blob' }),

  // Upload customer graphics PDFs
  uploadGraphics: (orderCode: string, files: File[], ownProductCode?: string) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    if (ownProductCode) {
      formData.append('ownProductCode', ownProductCode);
    }
    return api.post<{ orderCode: string; uploadCount: number; results: PrintUploadResult[]; message: string }>(
      `/print/upload/${orderCode}`,
      formData
    );
  },

  // Get current textures for an own product
  getTextures: (ownProductCode: string) =>
    api.get<PrintTexturesResponse>(`/print/textures/${ownProductCode}`),

  // Get PDF mapping configuration for a product
  getPdfMapping: (productId: number) =>
    api.get<PdfMappingResponse>(`/print/pdf-mapping/${productId}`),
};

// =============================================================================
// PRODUCT ASSEMBLY API
// =============================================================================

export interface AssemblyComponent {
  id: string;
  productId: number;
  code: string;
  type: string;
  name: string;
  image?: string;
  model3DPath?: string;
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number };
  scale: number;
  quantity: number;
  isMain: boolean;
  replaceable: boolean;
  sortOrder: number;
}

export interface AssemblyProduct {
  id: number;
  code: string;
  type: string;
  name: string;
  description?: string;
  image?: string;
  model3DPath?: string;
  isAssembly: boolean;
  isComponentOnly: boolean;
  showInProductList: boolean;
  group?: { id: number; name: string };
  defaultPosition: { x: number; y: number; z: number };
  defaultRotation: { x: number; y: number; z: number };
  defaultScale: number;
  components: AssemblyComponent[];
  subProductCount?: number;
}

export interface AvailableComponent {
  id: number;
  code: string;
  type: string;
  name: string;
  image?: string;
  model3DPath?: string;
  group?: string;
  isComponentOnly: boolean;
}

export const productAssemblyApi = {
  // List products (assemblies, components, or all)
  getProducts: (params?: { 
    type?: 'all' | 'assemblies' | 'components';
    search?: string;
    groupId?: number;
    page?: number;
    limit?: number;
  }) => api.get<{
    products: AssemblyProduct[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }>('/assembly/products', { params }),

  // Get single assembly details
  getAssembly: (productId: number) =>
    api.get<AssemblyProduct>(`/assembly/${productId}`),

  // Update assembly settings
  updateAssembly: (productId: number, data: {
    isAssembly?: boolean;
    isComponentOnly?: boolean;
    showInProductList?: boolean;
  }) => api.patch(`/assembly/${productId}`, data),

  // Add component to assembly
  addComponent: (productId: number, data: {
    subProductId: number;
    position?: { x: number; y: number; z: number };
    rotation?: { x: number; y: number; z: number };
    scale?: number;
    quantity?: number;
    isMain?: boolean;
    replaceable?: boolean;
  }) => api.post<AssemblyComponent>(`/assembly/${productId}/components`, data),

  // Update component position/settings
  updateComponent: (productId: number, componentId: string, data: {
    position?: { x: number; y: number; z: number };
    rotation?: { x: number; y: number; z: number };
    scale?: number;
    quantity?: number;
    isMain?: boolean;
    replaceable?: boolean;
    sortOrder?: number;
  }) => api.patch<AssemblyComponent>(`/assembly/${productId}/components/${componentId}`, data),

  // Remove component from assembly
  removeComponent: (productId: number, componentId: string) =>
    api.delete(`/assembly/${productId}/components/${componentId}`),

  // Bulk update component positions (save all at once)
  saveComponentPositions: (productId: number, components: {
    id: string;
    position: { x: number; y: number; z: number };
    rotation: { x: number; y: number; z: number };
    scale: number;
  }[]) => api.put(`/assembly/${productId}/components/positions`, { components }),

  // Get available components (products with 3D models)
  getAvailableComponents: (params?: { search?: string; groupId?: number }) =>
    api.get<AvailableComponent[]>('/assembly/available-components', { params }),
};

// =============================================================================
// WALL TEMPLATE API
// =============================================================================

export interface WallProduct {
  id: number;
  type: string;
  code: string;
  name: string;
  image?: string;
  isWallProduct: boolean;
  isEndPanel: boolean;
  allowCornerJoin: boolean;
  snapDistance: number;
  wallThickness: number;
  pdfWidth: number;
  pdfHeight: number;
  model3DPath?: string;
  price: number;
  moveXup: number;
  moveXdown: number;
  moveZup: number;
  moveZdown: number;
  rotationYup: number;
  rotationYdown: number;
}

export interface WallTemplateGroup {
  id: string;
  groupName: string;
  productTypes: string[];
  productIds: number[];
  totalWidth: number;
  scalePercent: number;
  templateFileName?: string;
  isDeletable: boolean;
  createdAt: string;
}

export interface FixedTemplate {
  id: string;
  groupName: string;
  fileName: string;
  productType: string;
  ownProductCode: string;
  isDeletable: boolean;
  isFixed: boolean;
}

export const wallApi = {
  // Get wall products and end panels
  getWallProducts: () =>
    api.get<WallProduct[]>('/wall/products'),

  // Get template groups for an order
  getTemplates: (orderCode: string) =>
    api.get<{
      wallGroups: WallTemplateGroup[];
      fixedTemplates: FixedTemplate[];
    }>(`/wall/templates/${orderCode}`),

  // Create a new wall template group
  createTemplateGroup: (orderCode: string, data: {
    groupName: string;
    productTypes: string[];
    productIds: number[];
  }) => api.post<WallTemplateGroup>(`/wall/templates/${orderCode}`, data),

  // Delete a wall template group
  deleteTemplateGroup: (orderCode: string, groupId: string) =>
    api.delete(`/wall/templates/${orderCode}/${groupId}`),

  // Download a wall template PDF
  downloadTemplate: (orderCode: string, groupId: string) =>
    api.get(`/wall/templates/${orderCode}/${groupId}/download`, { responseType: 'blob' }),

  // Get snapping configuration
  getSnapConfig: () =>
    api.get<{
      snapDistance: number;
      cornerAngle: number;
      snapPoints: string[];
      endPanelPositions: string[];
    }>('/wall/snap-config'),
};

// =============================================================================
// GROUP/CATEGORY API
// =============================================================================

export interface GroupText {
  id: string;
  languageId: string;
  languageName?: string;
  name: string;
}

export interface GroupProduct {
  id: number;
  type: string;
  code?: string;
  name: string;
  description?: string;
  image?: string;
  model3DPath?: string;
  price: number;
  is3D: boolean;
  isWallProduct?: boolean;
  isEndPanel?: boolean;
  pdfWidth?: number;
  pdfHeight?: number;
  order?: number;
}

export interface Group {
  id: number;
  name: string;
  picture?: string;
  menu: number;
  menuLabel: string;
  show: boolean;
  productCount?: number;
  texts: GroupText[];
  products?: GroupProduct[];
}

export interface SceneGroup {
  id: number;
  name: string;
  picture?: string;
  products: GroupProduct[];
}

export const groupApi = {
  // Get all groups
  getGroups: (params?: { menu?: number; show?: boolean; languageId?: string }) =>
    api.get<Group[]>('/groups', { params }),

  // Get groups for scene (Menu=3, with products)
  getGroupsForScene: (params?: { languageId?: string; currencyId?: string }) =>
    api.get<SceneGroup[]>('/groups/for-scene', { params }),

  // Get single group
  getGroup: (id: number) =>
    api.get<Group>(`/groups/${id}`),

  // Create group
  createGroup: (data: {
    picture?: string;
    menu?: number;
    show?: boolean;
    texts?: { languageId: string; name: string }[];
  }) => api.post<Group>('/groups', data),

  // Update group
  updateGroup: (id: number, data: {
    picture?: string;
    menu?: number;
    show?: boolean;
  }) => api.patch<Group>(`/groups/${id}`, data),

  // Update group texts
  updateGroupTexts: (id: number, texts: { languageId: string; name: string }[]) =>
    api.put<GroupText[]>(`/groups/${id}/texts`, { texts }),

  // Delete group
  deleteGroup: (id: number) =>
    api.delete(`/groups/${id}`),

  // Assign product to group
  assignProduct: (groupId: number, productId: number) =>
    api.post(`/groups/${groupId}/products`, { productId }),

  // Remove product from group
  removeProduct: (groupId: number, productId: number) =>
    api.delete(`/groups/${groupId}/products/${productId}`),

  // Reorder products in group
  reorderProducts: (groupId: number, productIds: number[]) =>
    api.put(`/groups/${groupId}/products/order`, { productIds }),
};

// =============================================================================
// SYSTEM ENDPOINTS
// =============================================================================

export const systemApi = {
  getLanguages: () => api.get<Language[]>('/admin/system/languages'),
  getCurrencies: () => api.get<Currency[]>('/admin/system/currencies'),
};

// =============================================================================
// CARPET TEXTURES
// =============================================================================

export interface CarpetTexture {
  id: string;
  name: string;
  description?: string;
  category?: string;
  filePath: string;
  thumbnailPath?: string;
  mimeType?: string;
  defaultScale: { x: number; y: number };
  isOwn?: boolean;
  type: 'system' | 'user';
}

export interface SceneCarpet {
  id: string;
  name: string;
  position: { x: number; y: number; z: number };
  rotationY: number;
  size: { width: number; depth: number };
  textureScale: { x: number; y: number };
  textureOffset: { x: number; y: number };
  textureRotation: number;
  opacity: number;
  color: string;
  sortOrder: number;
  isVisible: boolean;
  texture?: {
    id?: string;
    name?: string;
    filePath: string;
    isCustom?: boolean;
  };
  textureType: 'system' | 'user' | 'custom';
}

export interface CarpetTexturesResponse {
  systemTextures: CarpetTexture[];
  userTextures: CarpetTexture[];
  total: number;
  categories: string[];
}

export const carpetApi = {
  // Get available textures (system + user)
  getTextures: (params?: { search?: string; category?: string; includeUser?: boolean }) =>
    api.get<CarpetTexturesResponse>('/carpet-textures', { params }),

  // Get single texture
  getTexture: (id: string) =>
    api.get<CarpetTexture>(`/carpet-textures/${id}`),

  // Upload system texture (admin)
  uploadSystemTexture: (formData: FormData) =>
    api.post<{ texture: CarpetTexture }>('/carpet-textures/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // Upload user texture
  uploadUserTexture: (formData: FormData) =>
    api.post<{ texture: CarpetTexture }>('/carpet-textures/user/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // Delete user texture
  deleteUserTexture: (id: string) =>
    api.delete(`/carpet-textures/user/${id}`),

  // Get carpets in a scene
  getSceneCarpets: (sceneId: string, type: 'snapshot' | 'order' | 'guid' = 'order') =>
    api.get<{ carpets: SceneCarpet[] }>(`/carpet-textures/scene/${sceneId}`, { params: { type } }),

  // Add carpet to scene
  addCarpet: (sceneId: string, data: {
    name?: string;
    carpetTextureId?: string;
    userCarpetTextureId?: string;
    position?: { x: number; y: number; z: number };
    width?: number;
    depth?: number;
    textureScale?: { x: number; y: number };
  }, type: 'snapshot' | 'order' | 'guid' = 'order') =>
    api.post<{ carpet: { id: string; name: string } }>(`/carpet-textures/scene/${sceneId}`, data, { params: { type } }),

  // Update carpet
  updateCarpet: (carpetId: string, data: Partial<{
    name: string;
    carpetTextureId: string;
    userCarpetTextureId: string;
    position: { x: number; y: number; z: number };
    rotationY: number;
    width: number;
    depth: number;
    textureScale: { x: number; y: number };
    textureOffset: { x: number; y: number };
    textureRotation: number;
    opacity: number;
    color: string;
    sortOrder: number;
    isVisible: boolean;
  }>) =>
    api.put(`/carpet-textures/scene/carpet/${carpetId}`, data),

  // Delete carpet
  deleteCarpet: (carpetId: string) =>
    api.delete(`/carpet-textures/scene/carpet/${carpetId}`),

  // Reorder carpets
  reorderCarpets: (sceneId: string, carpetOrder: { id: string; sortOrder: number }[], type: 'snapshot' | 'order' | 'guid' = 'order') =>
    api.put(`/carpet-textures/scene/${sceneId}/reorder`, { carpetOrder }, { params: { type } }),
};

// =============================================================================
// SANDBOX (3D Preview without orders)
// =============================================================================

export interface SandboxListItem {
  id: string;
  name: string;
  description?: string;
  thumbnailPath?: string;
  isTemplate: boolean;
  templateCategory?: string;
  shareCode?: string;
  isPublic: boolean;
  productCount: number;
  snapshots?: { id: string; filePath: string; createdAt: string }[];
  snapshots3d?: { id: string; name: string | null; thumbnailPath: string | null; createdAt: string }[];
  createdAt: string;
  updatedAt: string;
  lastOpenedAt?: string;
}

export interface SandboxProduct {
  id: string;
  productId: number;
  quantity: number;
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number };
  texturePath?: string;
  /** Instance ids this one is welded to (Eco Wall runs). See @happy3d/joints/wallJoints. */
  joints?: string[];
  /** Customer artwork keyed by DESIGN template file name. */
  artwork?: Record<string, SandboxArtwork>;
  product?: {
    id: number;
    code: string;
    name: string;
    model3DPath?: string;
    /** Resolved by the API (low<ProductType> etc). Load via /upload/scene-asset/. */
    modelFile?: string | null;
    pdfWidth?: number;
    pdfHeight?: number;
    price?: number;
    /** Takes part in Eco Wall snapping. Seeded from the joint geometry below. */
    isWallProduct?: boolean;
    /** Joint offsets in MILLIMETRES, one pair per end of the segment. */
    moveXup?: number;
    moveXdown?: number;
    moveZup?: number;
    moveZdown?: number;
    /** Joint angles in RADIANS x 10000. */
    rotationYup?: number;
    rotationYdown?: number;
  };
}

/**
 * What the 3D Designer needs to draw one product the way the customer site does:
 * its DESIGN + PRODUCT3D files with print metadata, and its edge colour. Fetched
 * per scene load via sandboxApi.getProductRender, never stored in the scene.
 */
/**
 * Customer artwork on one print template of one product instance. `original` is
 * the upload exactly as sent, kept for print; `display` is the image the 3D model
 * wears (a PDF is rendered to JPEG server-side). Both are app-data paths.
 */
export interface SandboxArtwork {
  name: string;
  original: string;
  display: string;
  kind: 'pdf' | 'jpeg' | 'png';
  fileName: string;
  uploadedAt: string;
}

/** Ratios, not a sentence, so the UI can word it in the user's language. */
export interface SandboxArtworkWarning {
  code: 'aspect';
  actual: number;
  expected: number;
}

/** One panel of a whole-wall template, with the print size the template was drawn from. */
export interface SandboxWallGroupMember {
  instanceId: string;
  /** The panel's DESIGN template whose surface shows its strip of the wall. */
  template: string;
  productId: number;
  pdfWidth: number;
  pdfHeight: number;
  pdfValue: string | null;
}

/** A whole-wall print template: several panels, one design across them (legacy "Surface"). */
export interface SandboxWallGroup {
  id: string;
  name: string;
  /** Left to right, as the print runs. */
  members: SandboxWallGroupMember[];
  /** The panels plus the two 20mm side margins, mm. */
  totalWidth: number;
  /** The tallest panel, mm. */
  maxHeight: number;
  scale: number;
  template: string;
  templateName: string;
  createdAt: string;
  artwork: SandboxArtwork | null;
}

export interface SandboxRenderData {
  edgeColor: string | null;
  files: ProductFileMeta[];
}

export interface SandboxProp {
  id: string;
  propModelId: string;
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number };
  scale: number;
  propModel?: {
    id: string;
    name: string;
    modelFilePath?: string;
    thumbnail?: string;
  };
}

export interface SandboxSnapshot {
  id: string;
  filePath: string;
  createdAt: string;
}

export interface Snapshot3DListItem {
  id: string;
  name: string | null;
  thumbnailPath: string | null;
  createdAt: string;
}

export interface Snapshot3DDetail extends Snapshot3DListItem {
  state: {
    products?: any[];
    ownProducts?: any[];
    props?: any[];
    carpets?: any[];
    settings?: any;
  };
}

export interface SandboxDetail {
  id: string;
  name: string;
  description?: string;
  isTemplate: boolean;
  templateCategory?: string;
  shareCode?: string;
  isPublic: boolean;
  products: SandboxProduct[];
  ownProducts: any[];
  props: SandboxProp[];
  snapshots: SandboxSnapshot[];
  settings: {
    camera?: { alpha: number; beta: number; radius: number; target: { x: number; y: number; z: number } };
    lighting?: { intensity: number };
    wallGroups?: SandboxWallGroup[];
  };
  carpets: SceneCarpet[];
  thumbnailPath?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface SandboxTemplate {
  id: string;
  name: string;
  description?: string;
  thumbnailPath?: string;
  category?: string;
  productCount: number;
}

export const sandboxApi = {
  // List sandboxes
  list: (params?: { page?: number; limit?: number; templates?: boolean; search?: string; sort?: 'lastOpened' | 'nameAsc' | 'nameDesc' | 'createdDesc' }) =>
    api.get<{ data: SandboxListItem[]; total: number; page: number; limit: number }>('/sandbox', { params }),

  // Get templates
  getTemplates: (category?: string) =>
    api.get<{ templates: SandboxTemplate[]; categories: string[] }>('/sandbox/templates', { params: { category } }),

  // Create sandbox
  create: (data: { name: string; description?: string; templateId?: string }) =>
    api.post<{ sandbox: { id: string; name: string } }>('/sandbox', data),

  // Get sandbox
  get: (id: string) =>
    api.get<SandboxDetail>(`/sandbox/${id}`),

  // Update sandbox
  update: (id: string, data: Partial<{
    name: string;
    description: string;
    products: SandboxProduct[];
    ownProducts: any[];
    props: SandboxProp[];
    settings: any;
    isTemplate: boolean;
    templateCategory: string;
    isPublic: boolean;
    thumbnailPath: string;
    /** The floor mats as the scene shows them; the scene keeps exactly these (Update_181). */
    carpets: SceneCarpet[];
  }>) =>
    api.put<{ message: string; carpets?: SceneCarpet[] }>(`/sandbox/${id}`, data),

  // Delete sandbox
  delete: (id: string) =>
    api.delete(`/sandbox/${id}`),

  // Add product
  addProduct: (sandboxId: string, data: {
    productId: number;
    quantity?: number;
    position?: { x: number; y: number; z: number };
    rotation?: { x: number; y: number; z: number };
  }) =>
    api.post<{ product: SandboxProduct }>(`/sandbox/${sandboxId}/products`, data),

  // Update product
  updateProduct: (sandboxId: string, productInstanceId: string, data: Partial<{
    quantity: number;
    position: { x: number; y: number; z: number };
    rotation: { x: number; y: number; z: number };
    texturePath: string;
  }>) =>
    api.put(`/sandbox/${sandboxId}/products/${productInstanceId}`, data),

  // Remove product
  removeProduct: (sandboxId: string, productInstanceId: string) =>
    api.delete(`/sandbox/${sandboxId}/products/${productInstanceId}`),

  // Get carpets
  getCarpets: (sandboxId: string) =>
    api.get<{ carpets: SceneCarpet[] }>(`/sandbox/${sandboxId}/carpets`),

  // Add carpet
  addCarpet: (sandboxId: string, data: {
    name?: string;
    carpetTextureId?: string;
    userCarpetTextureId?: string;
    position?: { x: number; y: number; z: number };
    width?: number;
    depth?: number;
    textureScale?: { x: number; y: number };
  }) =>
    api.post(`/sandbox/${sandboxId}/carpets`, data),

  // Update carpet
  updateCarpet: (sandboxId: string, carpetId: string, data: any) =>
    api.put(`/sandbox/${sandboxId}/carpets/${carpetId}`, data),

  // Delete carpet
  deleteCarpet: (sandboxId: string, carpetId: string) =>
    api.delete(`/sandbox/${sandboxId}/carpets/${carpetId}`),

  // Update thumbnail
  // Render data for every distinct product in a scene, in one request. Keyed by
  // ProductID. See SandboxRenderData.
  getProductRender: (productIds: number[]) =>
    api.get<Record<number, SandboxRenderData>>('/sandbox/product-render', {
      params: { ids: productIds.join(',') },
    }),

  // Customer artwork, one per print template of one product instance.
  uploadArtwork: (
    sandboxId: string,
    instanceId: string,
    template: string,
    file: File,
    onProgress?: (percent: number) => void,
  ) => {
    const form = new FormData();
    form.append('template', template);
    form.append('file', file);
    return api.post<{ artwork: Record<string, SandboxArtwork>; warning: SandboxArtworkWarning | null }>(
      `/sandbox/${sandboxId}/products/${instanceId}/artwork`,
      form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        // A print PDF can be large and the server renders it before answering, so
        // there is a visible wait; report upload progress while it lasts.
        timeout: 0,
        onUploadProgress: (e) => {
          const total = e.total || file.size || 1;
          onProgress?.(Math.min(100, Math.round((e.loaded / total) * 100)));
        },
      },
    );
  },
  renameArtwork: (sandboxId: string, instanceId: string, template: string, name: string) =>
    api.patch<{ artwork: Record<string, SandboxArtwork> }>(
      `/sandbox/${sandboxId}/products/${instanceId}/artwork`,
      { template, name },
    ),
  removeArtwork: (sandboxId: string, instanceId: string, template: string) =>
    api.delete<{ artwork: Record<string, SandboxArtwork> }>(
      `/sandbox/${sandboxId}/products/${instanceId}/artwork`,
      { params: { template } },
    ),

  // Whole-wall print templates: one design across several panels.
  createWallGroup: (
    sandboxId: string,
    data: { name: string; members: { instanceId: string; template: string }[] },
  ) =>
    api.post<{ wallGroups: SandboxWallGroup[]; group: SandboxWallGroup }>(`/sandbox/${sandboxId}/wall-groups`, data),
  renameWallGroup: (sandboxId: string, groupId: string, name: string) =>
    api.patch<{ wallGroups: SandboxWallGroup[] }>(`/sandbox/${sandboxId}/wall-groups/${groupId}`, { name }),
  removeWallGroup: (sandboxId: string, groupId: string) =>
    api.delete<{ wallGroups: SandboxWallGroup[] }>(`/sandbox/${sandboxId}/wall-groups/${groupId}`),
  downloadWallTemplate: (sandboxId: string, groupId: string) =>
    api.get<Blob>(`/sandbox/${sandboxId}/wall-groups/${groupId}/template`, { responseType: 'blob' }),
  uploadWallArtwork: (
    sandboxId: string,
    groupId: string,
    file: File,
    onProgress?: (percent: number) => void,
  ) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<{ wallGroups: SandboxWallGroup[]; warning: SandboxArtworkWarning | null }>(
      `/sandbox/${sandboxId}/wall-groups/${groupId}/artwork`,
      form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 0,
        onUploadProgress: (e) => {
          const total = e.total || file.size || 1;
          onProgress?.(Math.min(100, Math.round((e.loaded / total) * 100)));
        },
      },
    );
  },
  removeWallArtwork: (sandboxId: string, groupId: string) =>
    api.delete<{ wallGroups: SandboxWallGroup[] }>(`/sandbox/${sandboxId}/wall-groups/${groupId}/artwork`),

  updateThumbnail: (id: string, thumbnailPath: string) =>
    api.post(`/sandbox/${id}/thumbnail`, { thumbnailPath }),

  // Snapshots
  addSnapshot: (id: string, filePath: string) =>
    api.post<{ snapshot: SandboxSnapshot }>(`/sandbox/${id}/snapshots`, { filePath }),
  deleteSnapshot: (id: string, snapshotId: string) =>
    api.delete<{ message: string }>(`/sandbox/${id}/snapshots/${snapshotId}`),

  // 3D Snapshots - full scene captures that can be restored in-place
  listSnapshots3D: (id: string) =>
    api.get<{ snapshots: Snapshot3DListItem[] }>(`/sandbox/${id}/snapshots3d`),
  getSnapshot3D: (id: string, snapshotId: string) =>
    api.get<Snapshot3DDetail>(`/sandbox/${id}/snapshots3d/${snapshotId}`),
  addSnapshot3D: (id: string, data: { name?: string; thumbnailPath?: string; state: unknown }) =>
    api.post<{ snapshot: Snapshot3DListItem }>(`/sandbox/${id}/snapshots3d`, data),
  deleteSnapshot3D: (id: string, snapshotId: string) =>
    api.delete<{ message: string }>(`/sandbox/${id}/snapshots3d/${snapshotId}`),

  // Share sandbox
  share: (id: string) =>
    api.post<{ shareCode: string; shareUrl: string }>(`/sandbox/${id}/share`),

  // Get by share code
  getByShareCode: (shareCode: string) =>
    api.get<{ id: string; name: string; description?: string }>(`/sandbox/share/${shareCode}`),
};

// =============================================================================
// ANALYTICS (VISITORS)
// =============================================================================

export type AnalyticsRange = '7d' | '30d' | '90d' | '365d';

export interface AnalyticsSummary {
  totalVisitors: number;
  totalPageViews: number;
  uniqueCountries: number;
  daily: Array<{ date: string; visitors: number; views: number }>;
}

export interface AnalyticsBreakdown {
  topCountries: Array<{ key: string; count: number }>;
  topPages: Array<{ key: string; count: number }>;
  topReferrers: Array<{ key: string; count: number }>;
  topProducts: Array<{ productId: number; name: string; count: number }>;
}

export interface LeadEventItem {
  id: string;
  sessionId: string;
  eventType: string;
  productId: number | null;
  country: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface AnalyticsLeads {
  counts: Array<{ type: string; count: number }>;
  events: LeadEventItem[];
}

export const analyticsApi = {
  summary: (range: AnalyticsRange = '7d') =>
    api.get<AnalyticsSummary>('/analytics/summary', { params: { range } }),
  breakdown: (range: AnalyticsRange = '7d', limit = 10) =>
    api.get<AnalyticsBreakdown>('/analytics/breakdown', { params: { range, limit } }),
  leads: (range: AnalyticsRange = '7d', limit = 50) =>
    api.get<AnalyticsLeads>('/analytics/leads', { params: { range, limit } }),
};

// =============================================================================
// WEBSITE (api/src/routes/website.routes.ts): the public sites' videos, posts,
// pages and pictures
// =============================================================================

export interface WebsiteSite {
  id: number;
  name: string;
  status: string;
  defaultLanguage: string;
  languages: string[];
  primaryHost: string | null;
}

export type WebsiteVideoKind = 'video' | 'assembly';

export interface WebsiteVideo {
  id: number;
  youtube: string;
  kind: WebsiteVideoKind;
  titles: Record<string, string>;
  productIds: number[];
  sortOrder: number;
  visible: boolean;
  onHomePage: boolean;
  updatedAt: string;
}

export interface WebsiteVideoInput {
  youtube: string;
  kind: WebsiteVideoKind;
  titles: Record<string, string>;
  productIds: number[];
  visible: boolean;
  onHomePage: boolean;
}

export type WebsiteContentKind = 'post' | 'page';
export type WebsiteContentStatus = 'draft' | 'published';

export interface WebsiteContentText {
  slug: string;
  title: string;
  summary: string;
  /** HTML in the format of api/src/utils/contentBlocks.ts. */
  body: string;
  /** Filled in by the translate button and not edited since. */
  machineTranslated: boolean;
  updatedAt?: string;
}

export interface WebsiteContentSummary {
  id: number;
  kind: WebsiteContentKind;
  pageKey: string | null;
  status: WebsiteContentStatus;
  publishedAt: string | null;
  sourceLanguage: string;
  coverMediaId: string | null;
  title: string;
  languages: string[];
  machineTranslated: string[];
  updatedAt: string;
}

export interface WebsiteContentItem extends WebsiteContentSummary {
  siteId: number;
  authorName: string | null;
  texts: Record<string, WebsiteContentText>;
}

export interface WebsiteContentSave {
  status: WebsiteContentStatus;
  publishedAt: string | null;
  sourceLanguage: string;
  coverMediaId: string | null;
  authorName: string | null;
  pageKey?: string;
  texts: Record<string, Omit<WebsiteContentText, 'updatedAt'>>;
}

export interface WebsiteMedia {
  id: string;
  fileName: string;
  mimeType: string;
  width: number | null;
  height: number | null;
  bytes: number;
  altText: string;
  createdAt: string;
}

export interface WebsiteProductOption {
  id: number;
  type: string;
  name: string;
}

export type WebsiteTranslation = { title: string; summary: string; body: string };

export const websiteApi = {
  sites: () => api.get<{ sites: WebsiteSite[] }>('/website/sites'),

  videos: (siteId: number) => api.get<{ videos: WebsiteVideo[] }>(`/website/sites/${siteId}/videos`),
  createVideo: (siteId: number, data: WebsiteVideoInput) =>
    api.post<{ video: WebsiteVideo }>(`/website/sites/${siteId}/videos`, data),
  updateVideo: (id: number, data: WebsiteVideoInput) => api.put<{ video: WebsiteVideo }>(`/website/videos/${id}`, data),
  deleteVideo: (id: number) => api.delete(`/website/videos/${id}`),
  orderVideos: (siteId: number, ids: number[]) => api.put(`/website/sites/${siteId}/videos/order`, { ids }),

  content: (siteId: number, kind: WebsiteContentKind) =>
    api.get<{ items: WebsiteContentSummary[] }>(`/website/sites/${siteId}/content`, { params: { kind } }),
  createContent: (siteId: number, data: { kind: WebsiteContentKind; pageKey?: string; sourceLanguage?: string }) =>
    api.post<{ item: WebsiteContentItem }>(`/website/sites/${siteId}/content`, data),
  getContent: (id: number) => api.get<{ item: WebsiteContentItem }>(`/website/content/${id}`),
  saveContent: (id: number, data: WebsiteContentSave) => api.put<{ item: WebsiteContentItem }>(`/website/content/${id}`, data),
  deleteContent: (id: number) => api.delete(`/website/content/${id}`),

  /** Machine translation of one text into other languages; can take a minute. */
  translate: (data: { source: string; targets: string[]; title: string; summary: string; body: string }) =>
    api.post<{ translations: Record<string, WebsiteTranslation>; warnings?: string[] }>('/website/translate', data, { timeout: 240000 }),

  media: (siteId: number) => api.get<{ media: WebsiteMedia[] }>(`/website/sites/${siteId}/media`),
  uploadMedia: (
    siteId: number,
    picture: { file: Blob; fileName: string; small: Blob | null; width: number; height: number; altText: string },
  ) => {
    const form = new FormData();
    form.append('file', picture.file, picture.fileName);
    if (picture.small) form.append('small', picture.small, `small-${picture.fileName}`);
    form.append('width', String(picture.width));
    form.append('height', String(picture.height));
    form.append('altText', picture.altText);
    return api.post<{ media: WebsiteMedia }>(`/website/sites/${siteId}/media`, form);
  },
  updateMedia: (id: string, altText: string) => api.patch<{ media: WebsiteMedia }>(`/website/media/${id}`, { altText }),
  deleteMedia: (id: string) => api.delete(`/website/media/${id}`),
  /** The picture's bytes; fetched with the login header, shown through an object URL. */
  mediaBlob: (id: string, small = false) =>
    api.get<Blob>(`/website/media/${id}`, { params: small ? { size: 'small' } : {}, responseType: 'blob' }),

  products: (lang: string) => api.get<{ products: WebsiteProductOption[] }>('/website/products', { params: { lang } }),
};

export default api;
